import type { LearningSchedule, LearningScheduleState } from './learning-schedule';
import type { PerformanceCalibration } from './performance-score';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import type { ClosePermissionReply, ClosePermissionRequest } from './close-permission';
import type { AppLinkRoute } from './app-links';
import type { CapturePointV6ContactInput, CapturePointV6LicenseView } from './v6-license-state';

export type {
  CapturePointV6ActivationRequestState,
  CapturePointV6AdmissionBlock,
  CapturePointV6ContactInput,
  CapturePointV6LicenseReachabilityState,
  CapturePointV6LicenseRuntimeState,
  CapturePointV6LicenseView,
} from './v6-license-state';

/** Whether an install-now request was accepted, and why not when it was refused. */
export type UpdateInstallResult = { started: true } | { started: false; reason: string };

/** The two deliberate appearances an operator can choose for this workstation. */
export type AppearanceMode = 'light' | 'dark';

/** A deliberate change to the size of the entire Paige interface. */
export type InterfaceZoomAction = 'in' | 'out' | 'reset';

/** The interface size and the safe range on the display holding this window. */
export type InterfaceZoomState = {
  level: number;
  percent: number;
  minimumPercent: number;
  maximumPercent: number;
  atMinimum: boolean;
  atMaximum: boolean;
};

/** Why the interface-size HUD appeared. */
export type InterfaceZoomEvent = InterfaceZoomState & {
  source: 'gesture' | 'display' | 'settings';
  limitReached: boolean;
};

/** Initial extraction preference; never authorizes learned adoption. */
export type ExtractionPreference = 'auto' | 'faster' | 'higher-accuracy';

/**
 * Which graphics cards Paige may use (Settings > Performance > Speed boost). "all", the
 * default, also takes a card fitted later; a list names cards by their driver UUID, and an empty
 * list means the processor only.
 */
export type GraphicsCardsAllowed = 'all' | string[];

/** What Paige runs on a card: the reader (Document details), the reasoner (Closer look), OCR (Searchable text). */
export type GraphicsCardTenant = 'reader' | 'reasoner' | 'ocr';

/** One graphics card this computer has, as Settings shows it. */
export interface GraphicsCardView {
  /** The driver's UUID ("GPU-…"): the identity the setting stores. */
  uuid: string;
  name: string;
  /** Total graphics memory, MiB; null when it could not be read. */
  totalMiB: number | null;
  /** What runs on it now, from the engine; null when the engine could not say. */
  tenants: GraphicsCardTenant[] | null;
}

/** The saved choice and the cards it applies to. */
export interface GraphicsCardsView {
  allowed: GraphicsCardsAllowed;
  cards: GraphicsCardView[];
}

/**
 * Which releases this workstation accepts. The stored values are the feed
 * directory names the publishing tools already use.
 *
 * `apps/desktop/src/main/update-channel.ts` owns everything about this setting —
 * the words, the file, and the rules a switch has to obey. Only the bare union
 * lives here, because the renderer and the preload bridge have to name it.
 */
export type UpdateChannel = 'stable' | 'beta';

/** One option in the software-updates setting, with the sentence beneath it. */
export type UpdateChannelChoice = { channel: UpdateChannel; title: string; detail: string };

/** The stored choice and the options to render, straight from the main process. */
export type UpdateChannelSetting = { channel: UpdateChannel; choices: UpdateChannelChoice[] };

/**
 * What changing the channel did — including to work that was already underway.
 *
 * `notice` is the sentence for the operator and is never null on a real change:
 * a switch that says nothing is indistinguishable from a switch that did
 * nothing, and both of those shipped once already.
 */
export type UpdateChannelChangeResult = {
  channel: UpdateChannel;
  /** Null only when the requested channel was already the stored one. */
  notice: string | null;
  status: UpdateStatus;
};

/**
 * Auto-update state surfaced to the renderer for the update banner.
 *
 * `downloading` and `ready` carry the channel the PACKAGE came from, which is
 * not the same fact as the setting's current value even when the two agree.
 * Reading the setting to label a package is what produced a banner offering a
 * beta build with "Stable" beside it; carrying the origin on the status means
 * the label cannot drift from the package no matter what the setting does next.
 *
 * Neither is nullable, and that is load-bearing rather than tidy. A package
 * whose origin cannot be established is never staged — it is proved against the
 * feed first or refused — so there is no "unknown" case for a guard downstream
 * to wave through.
 */
export type UpdateStatus =
  | { state: 'disabled'; reason: string }
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'downloading'; version: string; percent: number; channel: UpdateChannel }
  | { state: 'ready'; version: string; notes: string; channel: UpdateChannel }
  /**
   * The workstation is newer than anything on its channel, so nothing will
   * arrive until the channel passes it. A state rather than a flavour of "idle"
   * because it is the one case where being up to date needs explaining.
   */
  | {
      state: 'ahead';
      version: string;
      newestOnChannel: string;
      channel: UpdateChannel;
      notice: string;
    }
  | { state: 'failed'; reason: string };

/**
 * Background delivery of the large runtime components (the document reading
 * model, filing advisors and graphics-card libraries). The status bar shows it on
 * every computer until it completes. `message`/`remedy` are neutral, operator-safe
 * text and exist only while `state` is "failed".
 */
export type RuntimeComponentDownloadStatus = {
  readonly state: 'idle' | 'downloading' | 'failed' | 'complete';
  readonly receivedBytes: number;
  readonly totalBytes: number;
  /** 0..1 across every background component, weighted by bytes. */
  readonly fraction: number;
  /** Components not yet installed. */
  readonly remaining: number;
  readonly message?: string;
  readonly remedy?: string;
};

export const desktopChannels = {
  getRuntimeComponentStatus: 'runtime-components:get-status',
  retryRuntimeComponents: 'runtime-components:retry',
  runtimeComponentStatusChanged: 'runtime-components:status-changed',
  getRuntimeInfo: 'desktop:get-runtime-info',
  takeAppLink: 'app-links:take',
  appLinkWaiting: 'app-links:waiting',
  getAppearance: 'appearance:get',
  getTooltips: 'appearance:tooltips:get',
  setTooltips: 'appearance:tooltips:set',
  tooltipsChanged: 'appearance:tooltips:changed',
  getSupportAccess: 'support-access:get',
  setSupportAccess: 'support-access:set',
  /** Main to window: a job's list entry or documents changed outside the window (a support request, ADR-0088). */
  jobsChanged: 'jobs:changed',
  /** Main to window: run the handler a click runs, for a support request; answered on supportWindowReply. */
  supportWindowCommand: 'support-access:window-command',
  supportWindowReply: 'support-access:window-reply',
  /** Unattended processing (ADR-0090): a job's one watched folder, and what waits with nobody at the computer. */
  getWatchedFolder: 'watched-folder:get',
  setWatchedFolder: 'watched-folder:set',
  testWatchedFolder: 'watched-folder:test',
  watchedFolderStatus: 'watched-folder:status',
  chooseWatchedFolder: 'watched-folder:choose',
  setWatchedFolderEnabled: 'watched-folder:set-enabled',
  setWatchedFolderSeparation: 'watched-folder:set-separation',
  watchedFolderDetails: 'watched-folder:details',
  openWatchedFolder: 'watched-folder:open',
  /** Coordinator to window: the watched folders' status changed. */
  watchedFolderStatusChanged: 'watched-folder:status-changed',
  unattendedStatus: 'unattended:status',
  getAccentColors: 'appearance:accents:get',
  setAccentColor: 'appearance:accents:set',
  accentColorsChanged: 'appearance:accents:changed',
  setAppearance: 'appearance:set',
  appearanceChanged: 'appearance:changed',
  getInterfaceZoom: 'interface-zoom:get',
  changeInterfaceZoom: 'interface-zoom:change',
  interfaceZoomChanged: 'interface-zoom:changed',
  getEngineHealth: 'capture:get-engine-health',
  getWorkstationHealth: 'capture:get-workstation-health',
  retrySpeedBoost: 'capture:retry-speed-boost',
  getExtractionPreference: 'extraction:get-preference',
  setExtractionPreference: 'extraction:set-preference',
  getGraphicsCards: 'graphics-cards:get',
  setGraphicsCards: 'graphics-cards:set',
  getStorageLocation: 'workstation:get-storage-location',
  chooseStorageLocation: 'workstation:choose-storage-location',
  workstationHealthChanged: 'capture:workstation-health-changed',
  listJobs: 'jobs:list',
  getJobCardPreview: 'jobs:card-preview',
  getJobProcessingState: 'jobs:processing-state',
  setJobProcessingPaused: 'jobs:processing-pause',
  jobProcessingStateChanged: 'jobs:processing-state-changed',
  saveJob: 'jobs:save',
  resetLearning: 'jobs:reset-learning',
  setCalibration: 'jobs:set-calibration',
  previewDocumentTypeRemoval: 'jobs:document-type-removal-impact',
  removeDocumentType: 'jobs:remove-document-type',
  deleteJob: 'jobs:delete',
  exportJobTemplate: 'jobs:export-template',
  importJobTemplate: 'jobs:import-template',
  jobSetupFileWorking: 'jobs:setup-file-working',
  importSampleJobSetup: 'jobs:import-sample-setup',
  skipSampleReaderDownload: 'jobs:skip-sample-reader-download',
  sampleReaderDownload: 'jobs:sample-reader-download',
  listLegacyJobs: 'jobs:list-legacy',
  detectLegacyInstall: 'jobs:detect-legacy-install',
  listLegacyImports: 'jobs:list-legacy-imports',
  importLegacyJob: 'jobs:import-legacy',
  legacyImportStatus: 'jobs:legacy-import-status',
  analyzeLegacyImport: 'jobs:analyze-legacy-import',
  pauseLegacyImport: 'jobs:pause-legacy-import',
  chooseExportDestination: 'export:choose-destination',
  offerDefaultExportDestination: 'export:offer-default-destination',
  chooseExceptionsFolder: 'export:choose-exceptions-folder',
  openExceptionsFolder: 'export:open-exceptions-folder',
  getExportFolderDefaults: 'export:get-folder-defaults',
  getExportSettings: 'export:get-settings',
  getExportCapabilities: 'export:get-capabilities',
  getStarterFilingPlan: 'export:get-starter-filing-plan',
  suggestFilingPlan: 'export:suggest-filing-plan',
  previewFilingPlan: 'export:preview-filing-plan',
  saveExportSettings: 'export:save-settings',
  previewExport: 'export:preview',
  runExport: 'export:run',
  listExportedDocuments: 'export:list-documents',
  openExportedDocument: 'export:open-document',
  returnExportToWork: 'export:return-to-work',
  cancelContentCentralExport: 'export:cancel-content-central',
  retryExport: 'export:retry',
  retryContentCentralExport: 'export:retry-content-central',
  listDestinationConnections: 'destinations:list-connections',
  connectContentCentral: 'destinations:connect-content-central',
  recheckContentCentral: 'destinations:recheck-content-central',
  replaceContentCentralKey: 'destinations:replace-content-central-key',
  removeContentCentral: 'destinations:remove-content-central',
  listContentCentralCatalogs: 'destinations:list-content-central-catalogs',
  getContentCentralCatalog: 'destinations:get-content-central-catalog',
  proposeContentCentralBinding: 'destinations:propose-content-central-binding',
  cancelContentCentralProposalIntent: 'destinations:cancel-content-central-proposal-intent',
  createContentCentralJob: 'destinations:create-content-central-job',
  findContentCentralJob: 'destinations:find-content-central-job',
  proposeContentCentralCatalog: 'destinations:propose-content-central-catalog',
  getPendingContentCentralProposal: 'destinations:pending-content-central-proposal',
  discardPendingContentCentralProposal: 'destinations:discard-content-central-proposal',
  applyContentCentralCatalog: 'destinations:apply-content-central-catalog',
  verifyContentCentralCatalogApply: 'destinations:verify-content-central-catalog-apply',
  getJobDestinationBinding: 'destinations:get-job-binding',
  previewContentCentralFiling: 'destinations:preview-content-central-filing',
  getLatestBatch: 'capture:get-latest-batch',
  getLatestBatchOverview: 'capture:get-latest-batch-overview',
  getJobWorkSummary: 'capture:get-job-work-summary',
  getReviewOutcomes: 'capture:get-review-outcomes',
  getLiveCaptureProgress: 'capture:get-live-capture-progress',
  getBatchDocumentIndex: 'capture:get-batch-document-index',
  getBatchDocumentWindow: 'capture:get-batch-document-window',
  getBatchDocument: 'capture:get-batch-document',
  retryDocuments: 'capture:retry-documents',
  reprocessReviewed: 'capture:reprocess-reviewed',
  reextractPreparedDocument: 'capture:reextract-prepared-document',
  findDocumentTable: 'capture:find-document-table',
  reclassifyPreparedDocument: 'capture:reclassify-prepared-document',
  reclassifyBoundaryDocuments: 'capture:reclassify-boundary-documents',
  refreshBatchSchema: 'capture:refresh-batch-schema',
  searchPages: 'capture:search-pages',
  getScannerSources: 'capture:get-scanner-sources',
  chooseRepresentativeFiles: 'capture:choose-representative-files',
  chooseDocumentFolder: 'capture:choose-document-folder',
  importFolderDocuments: 'capture:import-folder-documents',
  continueFolderAnalysis: 'capture:continue-folder-analysis',
  resumeFolderAnalysis: 'capture:resume-folder-analysis',
  abandonFolderAnalysisOperation: 'capture:abandon-folder-analysis-operation',
  stopFolderAnalysisOperation: 'capture:stop-folder-analysis-operation',
  getFolderAnalysisStatus: 'capture:get-folder-analysis-status',
  includeRemainingFolderDocuments: 'capture:include-remaining-folder-documents',
  getFolderAnalysisEvidence: 'capture:get-folder-analysis-evidence',
  saveFolderAnalysisEvidence: 'capture:save-folder-analysis-evidence',
  stageFolderImport: 'capture:stage-folder-import',
  stageFolderWaves: 'capture:stage-folder-waves',
  folderAnalysisProgress: 'capture:folder-analysis-progress',
  stageDocuments: 'capture:stage-documents',
  stageScan: 'capture:stage-scan',
  cancelScan: 'capture:cancel-scan',
  scanProgress: 'capture:scan-progress',
  getSimulatedScanners: 'capture:get-simulated-scanners',
  startSimulatedScan: 'capture:start-simulated-scan',
  stopSimulatedScan: 'capture:stop-simulated-scan',
  simulatedScanProgress: 'capture:simulated-scan-progress',
  createPlainBreakSheets: 'capture:create-plain-break-sheets',
  createFieldBreakSheets: 'capture:create-field-break-sheets',
  listBreakSheetRuns: 'capture:list-break-sheet-runs',
  openBreakSheetRun: 'capture:open-break-sheet-run',
  listStagedCaptures: 'capture:list-staged-captures',
  processStagedCapture: 'capture:process-staged-capture',
  captureProcessingProgress: 'capture:processing-progress',
  discardStagedCapture: 'capture:discard-staged-capture',
  retryStagedCapture: 'capture:retry-staged-capture',
  releaseHeldCapture: 'capture:release-held-capture',
  recoverRetainedScans: 'capture:recover-retained-scans',
  readPagePreview: 'capture:read-page-preview',
  getUpdateStatus: 'updates:get-status',
  installUpdateNow: 'updates:install-now',
  checkForUpdateNow: 'updates:check-now',
  updateStatusChanged: 'updates:status-changed',
  getUpdateChannel: 'updates:get-channel',
  setUpdateChannel: 'updates:set-channel',
  copyText: 'desktop:copy-text',
  showCopyMenu: 'desktop:show-copy-menu',
  saveCorrection: 'capture:save-correction',
  saveDocumentCorrection: 'capture:save-document-correction',
  saveDocumentCorrections: 'capture:save-document-corrections',
  undoFieldEdit: 'capture:undo-field-edit',
  waitForLayoutCatchUp: 'capture:wait-for-layout-catch-up',
  saveBoundaryMutation: 'capture:save-boundary-mutation',
  boundaryPagesLike: 'capture:boundary-pages-like',
  removePage: 'capture:remove-page',
  restorePage: 'capture:restore-page',
  deleteDocuments: 'capture:delete-documents',
  restoreDocuments: 'capture:restore-documents',
  listDeletedDocuments: 'capture:list-deleted-documents',
  turnPage: 'capture:turn-page',
  turnPageProgress: 'capture:turn-page-progress',
  suggestDocumentType: 'capture:suggest-document-type',
  applyConfirmedTypeExamples: 'capture:apply-confirmed-type-examples',
  listJobSuggestions: 'capture:list-job-suggestions',
  getLearningProgress: 'capture:get-learning-progress',
  getLearningAcceleration: 'capture:get-learning-acceleration',
  getLearningCapability: 'capture:get-learning-capability',
  getOperatorCorrections: 'capture:get-operator-corrections',
  getDocumentTypeNames: 'capture:get-document-type-names',
  getReviewImpactProfile: 'capture:get-review-impact-profile',
  previewReviewRelease: 'capture:preview-review-release',
  autoTuneReviewBars: 'capture:auto-tune-review-bars',
  getLearningEffort: 'capture:get-learning-effort',
  getJobSpeedHistory: 'capture:get-job-speed-history',
  measureJobNow: 'capture:measure-job-now',
  measureJobProgress: 'capture:measure-job-progress',
  getLearningScorecard: 'capture:get-learning-scorecard',
  listJobSuggestionHistory: 'capture:list-job-suggestion-history',
  listSelfAppliedSuggestions: 'capture:list-self-applied-suggestions',
  applyLearningToWaitingWork: 'capture:apply-learning-to-waiting-work',
  listHeldWorkReleases: 'capture:list-held-work-releases',
  reopenHeldWorkRelease: 'capture:reopen-held-work-release',
  noteOpenDocument: 'capture:note-open-document',
  jobAccuracy: 'capture:job-accuracy',
  learnFieldFaults: 'capture:learn-field-faults',
  startLearning: 'capture:start-learning',
  learningStatus: 'capture:learning-status',
  learningEligibility: 'capture:learning-eligibility',
  teachingRecommendations: 'capture:teaching-recommendations',
  learningHistory: 'capture:learning-history',
  layoutReaderModels: 'learning:layout-reader-models',
  learningFieldResults: 'capture:learning-field-results',
  learningTableResults: 'capture:learning-table-results',
  learningWorkstation: 'capture:learning-workstation',
  learningScheduleState: 'capture:learning-schedule-state',
  initializeLearningSchedule: 'capture:initialize-learning-schedule',
  setLearningSchedule: 'capture:set-learning-schedule',
  stopLearning: 'capture:stop-learning',
  decideJobSuggestion: 'capture:decide-job-suggestion',
  applyJobSuggestion: 'capture:apply-job-suggestion',
  undoJobSuggestion: 'capture:undo-job-suggestion',
  batchHasCorrections: 'capture:batch-has-corrections',
  getBatchReprocessImpact: 'capture:get-batch-reprocess-impact',
  dismissBatchWarning: 'capture:dismiss-batch-warning',
  interpretField: 'capture:interpret-field',
  importDataTable: 'lookups:import-data-table',
  listDataTables: 'lookups:list-data-tables',
  saveLookupRule: 'lookups:save-rule',
  deleteLookupRule: 'lookups:delete-rule',
  deleteDataTable: 'lookups:delete-data-table',
  renameDataTable: 'lookups:rename-data-table',
  testLookup: 'lookups:test',
  previewDataTable: 'lookups:preview-data-table',
  validateLookupRule: 'lookups:validate-rule',
  fieldCanMatchColumn: 'lookups:field-can-match-column',
  measureRecentReadings: 'lookups:measure-recent-readings',
  openReviewPanel: 'desktop:open-review-panel',
  closeReviewPanel: 'desktop:close-review-panel',
  closePermissionRequest: 'desktop:close-permission-request',
  closePermissionReply: 'desktop:close-permission-reply',
  getDetachedReviewPanels: 'desktop:get-detached-review-panels',
  reviewPanelStateChanged: 'desktop:review-panel-state-changed',
  getV6LicenseContact: 'v6-license:get-contact',
  getV6LicenseState: 'v6-license:get-state',
  getPageAllowance: 'v6-license:page-allowance',
  checkInPages: 'v6-license:check-in-pages',
  buyPages: 'v6-license:buy-pages',
  pageAllowanceChanged: 'v6-license:page-allowance-changed',
  refreshV6License: 'v6-license:refresh',
  extendV6Trial: 'v6-license:extend-trial',
  requestV6Activation: 'v6-license:request-activation',
  requestV6MoreTime: 'v6-license:request-more-time',
  v6LicenseStateChanged: 'v6-license:state-changed',
  getEngagement: 'engagement:get',
  setEngagementConsent: 'engagement:set-consent',
  noteOnboardingMilestone: 'engagement:note-milestone',
  adoptOnboardingMilestones: 'engagement:adopt-milestones',
  startCheckout: 'engagement:start-checkout',
  lookupPartnerCode: 'engagement:lookup-partner-code',
  requestInvoice: 'engagement:request-invoice',
  getPendingInvoiceRequest: 'engagement:get-pending-invoice',
  engagementChanged: 'engagement:changed',
  getEmailAsks: 'email-asks:get',
  sendEmailAsk: 'email-asks:send',
  turnOffEmailAsk: 'email-asks:turn-off',
  emailAsksChanged: 'email-asks:changed',
  trackTelemetry: 'telemetry:track',
  getTelemetry: 'telemetry:get',
  describeBenchmarkMachine: 'benchmark:describe-machine',
  recordBenchmarkRun: 'benchmark:record-run',
  getBenchmarkHistory: 'benchmark:get-history',
  startSpeedCheck: 'benchmark:start-speed-check',
  cancelSpeedCheck: 'benchmark:cancel-speed-check',
  getSpeedCheckState: 'benchmark:get-speed-check-state',
  getSpeedCheckAvailability: 'benchmark:get-speed-check-availability',
  getSpeedCheckDetails: 'benchmark:get-speed-check-details',
  getPerformanceCalibration: 'benchmark:get-performance-calibration',
  getGraphicsMemoryAtTestStart: 'benchmark:get-graphics-memory-at-test-start',
  getSpeedCheckSchedule: 'benchmark:get-speed-check-schedule',
  setSpeedCheckSchedule: 'benchmark:set-speed-check-schedule',
  speedCheckStateChanged: 'benchmark:speed-check-state-changed',
  getSpeedTestSamples: 'benchmark:get-speed-test-samples',
  speedTestSamplesProgress: 'benchmark:speed-test-samples-progress',
  getCluster: 'cluster:get',
  setClusterWatching: 'cluster:set-watching',
  createCluster: 'cluster:create',
  joinCluster: 'cluster:join',
  cancelClusterJoin: 'cluster:cancel-join',
  admitIntoCluster: 'cluster:admit',
  ignoreClusterRequest: 'cluster:ignore-request',
  removeFromCluster: 'cluster:remove',
  setClusterParticipating: 'cluster:set-participating',
  leaveCluster: 'cluster:leave',
  clusterChanged: 'cluster:changed',
} as const;

/**
 * A complaint the reader can raise. The engine writes these; this side branches
 * on them.
 *
 * **This list must hold every code `FindingCode` declares.** It was short of
 * three for a long time, and a code missing here is not inert: it falls past
 * every branch on this side to "holds forever", which is how a document ends up
 * in a queue no edit can empty. `tests/finding-clearing-tests` fails when the
 * two vocabularies differ, and `finding-clearing.ts` will not compile until a
 * code added here has a clearing rule.
 */
export const FINDING = {
  lineItemArithmetic: 'line_item_arithmetic',
  lineItemReadIssue: 'line_item_read_issue',
  lineItemsPossiblyIncomplete: 'line_items_possibly_incomplete',
  partialSemanticProcessing: 'partial_semantic_processing',
  ungroundedExtraction: 'ungrounded_extraction',
  semanticAliasConflict: 'semantic-alias-conflict',
  pageRotationGuessed: 'page_rotation_guessed',
  /** A sideways page was turned. A record of what happened, not a complaint. */
  pageRotationApplied: 'page_rotation_applied',
  misplacedValue: 'misplaced_value',
  /**
   * A page of this document was never read by the recogniser. Unlike the other
   * codes here, no answer an operator can give clears it — the repair is a
   * re-read, which is why the surface that shows it must offer one.
   */
  pageNotRead: 'page_not_read',
  /** This document's reading never reached the model at all. */
  modelUnreachable: 'model_unreachable',
  /** Part of it was read and part of it never reached the model. */
  modelPartiallyUnreachable: 'model_partially_unreachable',
} as const;

/**
 * The source a set-aside value carries, so this side can tell a field the page
 * left blank from one whose value was found under someone else's label.
 * Mirrors DocumentPipeline.MisplacedValueSource; the two must stay identical.
 */
export const MISPLACED_VALUE_SOURCE = "set aside - printed where another field's label points";

/** The kinds of improvement this product can learn and propose. The engine names
 *  the kind; this side branches on it to describe what applying it would do. */
export const SUGGESTION = {
  barcodeField: 'barcode-field',
  fieldFormat: 'field-format',
  boundaryBarcode: 'boundary-barcode',
  boundaryOpening: 'boundary-opening',
  boundaryContinuation: 'boundary-continuation',
  separatorSheet: 'separator-sheet',
  lineItemShift: 'line-item-shift',
  /* The engine has minted these since field instructions were built, and this
     object did not admit the kind existed — so the badge fell through to the
     generic "Improve this job" for the one card that teaches the reader HOW to
     read a field. Second time this list has fallen behind the engine; the first
     was line-item-shift, which is what the note above `kind` records.
     `tests/suggestion-vocabulary-tests` now reads SuggestionKind out of
     Vocabulary.cs and fails when the two disagree, so there is no third time. */
  fieldInstruction: 'field-instruction',
  lineItemQuantityBalance: 'line-item-quantity-balance',
  tableReading: 'table-reading',
  documentType: 'document-type',
  newField: 'new-field',
} as const;

/**
 * Where a suggestion's `estimatedPrecision` came from. Mirrors `SuggestionPrecision`
 * in the engine's `Vocabulary.cs`, which is where the reasoning lives.
 *
 * Four kinds of number share one field, and the drawer ranks on it — so an asserted
 * 0.85 sorts above a measured 0.80. Anything that shows the number must say which
 * kind it is showing.
 */
export const SUGGESTION_PRECISION = {
  /** A held-out split scored it. Comparing two of these means something. */
  measured: 'measured',
  /** A trial's verdict as 1.0 or 0.0, not a rate — so it tops any ranking by construction. */
  trial: 'trial',
  /** The evidence IS the condition; 1.0 could not have come out lower. */
  definitional: 'definitional',
  /** A constant nobody measured. */
  asserted: 'asserted',
} as const;

/**
 * What each suggestion kind would DO, said as its verb. The title carries the
 * specifics; this badge answers "what kind of change am I being asked for"
 * before the operator reads a word of it.
 */
export const SUGGESTION_ACTION: Record<(typeof SUGGESTION)[keyof typeof SUGGESTION], string> = {
  'barcode-field': 'Read a field from its barcode',
  'field-format': 'Check values against a format',
  'boundary-barcode': 'Split documents on a barcode',
  'boundary-opening': 'Improve where documents start',
  'boundary-continuation': 'Improve multi-page grouping',
  'separator-sheet': 'Detect break sheets',
  'line-item-shift': 'Fix table columns',
  'field-instruction': 'Tell the reader how to read a field',
  'line-item-quantity-balance': 'Repair table quantities',
  'table-reading': 'Improve table reading',
  'document-type': 'Add a document type',
  'new-field': 'Add a field',
};

/** How a job decides where one document ends and the next begins. */
export const SEPARATION = {
  automatic: 'automatic',
  existingBarcode: 'existing-barcode',
  automaticBarcode: 'automatic-barcode',
  capturePointSeparator: 'capturepoint-separator',
  /** Every source file is one document; nothing splits inside a file (engine SeparationMode.FilePerDocument). */
  filePerDocument: 'file-per-document',
} as const;

/**
 * Whether a file's source separates documents itself (engine SourceSeparation; ADR-0091). Under
 * Automatic a `whole` file is one document unless the job's confirmations adopt otherwise; a
 * `stack` is split by the automatic detector; `unknown` is the behaviour before hints existed.
 */
export const SOURCE_SEPARATION = { whole: 'whole', stack: 'stack', unknown: 'unknown' } as const;
export type SourceSeparation = (typeof SOURCE_SEPARATION)[keyof typeof SOURCE_SEPARATION];
/** One file's source on a process/append request (engine `fileSources`, in the order of `files`). */
export type FileSource = { separation: SourceSeparation; kind: string };

export type DesktopRuntimeInfo = {
  appVersion: string;
  isPackaged: boolean;
  platform: NodeJS.Platform;
};

export type EngineHealth = {
  version: string;
  ocrProvider: string;
  barcodeProvider: string;
  ocrReady: boolean;
  ocrDetail?: string;
  semanticProvider: string;
  semanticReady: boolean;
  semanticDetail?: string;
  /** The page reader's lanes as numbers (the engine's OcrLaneState); absent from an engine too old to report them. */
  ocrLanes?: OcrLaneState | null;
};

/**
 * The page reader's lanes: state "warming", "ready" (every planned lane started), "partial", "resting", "not-started",
 * "unavailable" or "not-installed"; planned is the profile's allocation, capable what this machine could run.
 */
export type OcrLaneState = { state: string; planned: number; started: number; capable: number };

/**
 * What each accelerated part of the pipeline is really running on.
 *
 * `verified-gpu` is the only verdict that asserts a graphics card is doing the
 * work, and the engine only ever issues it alongside the measurements that
 * establish it. `running-on-cpu` is the expensive one: a card is installed, this
 * stage should be on it, and it is not. `by-design-cpu` is not a fault —
 * barcode decoding and page rendering are processor work on every workstation,
 * and a machine with no card reads documents on its processor correctly.
 */
export type AcceleratorVerdict =
  'verified-gpu' | 'running-on-cpu' | 'unavailable' | 'by-design-cpu';

/** One measured fact behind a verdict, already formatted with its units. */
export type AcceleratorEvidence = { label: string; value: string; note?: string | null };

/**
 * A throughput this workstation actually recorded.
 *
 * Never measured to fill this in. The health check reads stage timings back out
 * of the last completed batch, because a check that starts a benchmark is one
 * nobody runs while the machine is busy — which is when the question gets asked.
 */
export type MeasuredRate = {
  pagesPerMinute: number;
  pages: number;
  measuredAt: string;
  source: string;
};

export type SpeedBoostResult = {
  state: 'on' | 'off' | 'problem' | 'unknown';
  /** When this result was established, not the latest idle/status poll. */
  observedAt: string;
  verifiedAt?: string;
  reason?: string | null;
};

export type AcceleratorReport = {
  speedBoostConfigurationKey?: string | null;
  speedBoostFailureReason?: string | null;
  /** Remembered capability; the native verdict still describes execution evidence. */
  speedBoost?: SpeedBoostResult;
  /** Proven residency before deliberate idle release, never a claim of current execution. */
  speedBoostVerifiedBeforeIdle?: boolean;
  component: string;
  title: string;
  expectedDevice: string;
  actualDevice: string;
  verdict: AcceleratorVerdict;
  evidence: AcceleratorEvidence[];
  lastMeasuredRate?: MeasuredRate | null;
  reason?: string | null;
  cost?: string | null;
  remedy?: string | null;
};

/**
 * How much of the last batch never reached the reading model.
 *
 * A different question from every verdict beside it: those ask which device did
 * the work, this asks whether there was any work at all. A workstation can be
 * perfectly accelerated and still have read nothing, and a batch of empty fields
 * looks the same either way until this number is on the screen.
 */
export type ModelReachabilityReport = {
  batchId: string;
  observedAt: string;
  documentsAttempted: number;
  unreachableDocuments: number;
  firstReason?: string | null;
  unreachableDocumentNames: string[];
};

export type WorkstationHealthReport = {
  speedBoostHardwareKey?: string | null;
  graphicsCards?: string[] | null;
  checkedAt: string;
  engineVersion: string;
  verdict: AcceleratorVerdict;
  summary: string;
  components: AcceleratorReport[];
  /** The whole report as plain text, for pasting into a support conversation. */
  diagnostic: string;
  /** The last batch's not-read count, absent before this engine has read a batch. */
  modelReachability?: ModelReachabilityReport | null;
  /** Installed intended CPU capability, separate from measured runtime/device evidence. */
  processorOnlyByDesign?: boolean;
  /** Supported CUDA hardware is reachable; actual reader use is checked separately. */
  enhancedOcrSupported?: boolean;
  /** An NVIDIA card with enough memory for the AI reviewer (Autopilot's AI check). */
  aiReviewSupported?: boolean;
  /** The AI reviewer's model, runtime and vision projector are installed. */
  aiReviewerInstalled?: boolean;
};

/**
 * Whether this computer reads with the processor by design. A missing health answer and a
 * graphics installation that fell back to the processor are not that.
 */
export const processorOnlyByDesign = (
  health: WorkstationHealthReport | null | undefined,
): boolean =>
  typeof health?.processorOnlyByDesign === 'boolean'
    ? health.processorOnlyByDesign
    : (health?.components.some(
        (component) =>
          component.component === 'extraction' && component.verdict === 'by-design-cpu',
      ) ?? false);

/**
 * Graphics memory when a speed test started: what was free on the card and which other apps held it.
 * Friendly app names and sizes only; no process paths or ids reach the renderer.
 */
export type GraphicsMemoryAtTestStart = {
  capturedAt: string;
  /** Free graphics memory: nvidia-smi's on NVIDIA, as the engine reads it; on the selected AMD or Intel card, its
   * dedicated memory less what Windows counts in use on it (freeOnCard); null when neither answers. */
  freeMegabytes: number | null;
  /** Everything on the card that is not Paige's: other apps, everyday apps and Windows itself (the card's own total). */
  othersMegabytes: number;
  /**
   * Apps a person could close, each summed across its processes, biggest first; never Windows,
   * an everyday app (a browser, email, chat, Office: EVERYDAY_APPS) or Paige.
   */
  apps: Array<{ name: string; megabytes: number }>;
  /**
   * How the test's engine plans its reader on this card (its reader-memory-plan command), so the
   * speed advice uses this card's own figures (owner items 122, 145); absent from an engine before it.
   */
  readerPlan?: ReaderMemoryPlan | null;
};

/**
 * The document engine's reader plan for this card: `reserveMegabytes`, what one reader needs free to
 * start on it, and `steps`, from how much free graphics memory (nothing else of Paige's on the
 * card) it takes how many documents at once, growing. Below the first step it does not start on the card.
 */
export type ReaderMemoryPlan = {
  reserveMegabytes: number;
  steps: Array<{ slots: number; freeMegabytes: number }>;
};

/** That reading with the test it was taken for: only a test that finished keeps one. */
export type GraphicsMemoryAtScoredTest = GraphicsMemoryAtTestStart & { runId: string };

/** What the computer check's "Try again" measured and did. */
export type SpeedBoostRecheck = {
  report: WorkstationHealthReport;
  /** Free graphics memory measured just now, or null when it could not be measured (no NVIDIA card). */
  freeGraphicsMemoryMegabytes: number | null;
  /** One document reader fits in that memory. */
  readerFits: boolean;
  /** on | next-document | busy | still-processor | not-enough-graphics-memory | graphics-memory-not-measured | not-checked */
  outcome: string;
  checkedAt: string;
};

/**
 * A way pages get in that the host offers beside files and folders. On the desktop every one is a
 * scanner and the tile reads "Scan paper" over the device's name. A host that offers something else
 * says so: `title` is the tile's heading and `icon` picks its art, so Paige's e-mail-to-this-job
 * source is not a second tile reading "Scan paper" (docs/engineering/workbench-port.md).
 */
export type ScannerSource = {
  name: string;
  hostArchitecture: 'x64' | 'x86';
  title?: string;
  icon?: 'scanner' | 'email' | 'folder';
};
export type ScannerDiscovery = { sources: ScannerSource[]; errors: string[] };
export type FolderSelection = {
  canceled: boolean;
  token?: string;
  folderName?: string;
  supportedFileCount?: number;
  analysisFileCount?: number;
};

export type JobFieldDefinition = {
  id: string;
  name: string;
  semanticId?: string | null;
  kind: string;
  required: boolean;
  readText: boolean;
  readBarcode: boolean;
  description?: string | null;
  multiple: boolean;
  position: number;
  barcodeSymbology?: string | null;
  barcodeValuePrefix?: string | null;
  valuePattern?: string | null;
  formatExample?: string | null;
  defaultValue?: string | null;
  /** How to find this field, in words the reader is given. Written by the learner. */
  readingInstruction?: string | null;
  /** Native learned strategy; not a customer-authored setting. */
  tableReadingPlan?: TableReadingPlan | null;
  /** The lookup rules this field is subject to, as the engine stores them. */
  lookups?: FieldLookupAssignment[] | null;
};

/** What happens when no row matches: keep the read value, blank it, or send it to review. */
export type NoMatchBehaviour = 'keep' | 'blank' | 'review';

/**
 * One lookup rule a field is subject to.
 *
 * Which column fills which field lives on the rule (`LookupRuleDefinition.returns`);
 * which reading finds the row lives here, because one rule serves many document types.
 * With no `sourceFieldId` the field's own reading finds the row — a field filled from
 * the column it is matched on is that way saved with the list's spelling.
 */
export type FieldLookupAssignment = {
  ruleId: string;
  sourceFieldId?: string | null;
  noMatchBehaviour?: NoMatchBehaviour;
};

export type DataTableSummary = {
  id: string;
  jobId: string;
  name: string;
  sourceFileName: string;
  rowCount: number;
  columnNames: string[];
  uploadedAt: string;
  checksum: string;
  usedByRuleNames: string[];
};

/** What one column looks like, and which value kind that shape argues for. */
export type DataTableColumnProfile = {
  column: string;
  proposedValueKind: string;
  confidence: number;
  reasons: string[];
  contrary: string[];
  profiledRowCount: number;
  nonEmptyCount: number;
  distinctCount: number;
  meanTokenCount: number;
  whitespaceRatio: number;
  digitRatio: number;
  lengthVariation: number;
  uniqueRatio: number;
  sharedPrefix?: string | null;
};

export type DataTableImportResult = {
  table: DataTableSummary;
  columns: DataTableColumnProfile[];
  warnings: string[];
};

/**
 * A page of an imported table's actual rows.
 *
 * `totalRowCount` counts the stored rows and is the number a pager can reach;
 * prefer it over `table.rowCount`, which is written once at import.
 */
export type DataTablePreview = {
  table: DataTableSummary;
  columnNames: string[];
  rows: string[][];
  /** Rows the page is drawn from: every row, or with a search only the matching ones. */
  totalRowCount: number;
  offset: number;
  limit: number;
  /** Each returned row's place in the file, the same number a lookup reports. */
  rowNumbers?: number[];
  /** The search that was applied, trimmed; absent or null when none was. */
  query?: string | null;
};
export type DataTableCatalog = {
  tables: DataTableSummary[];
  rules: LookupRuleDefinition[];
};

/**
 * Which switches the "spelled the same way" rung has, per kind of value.
 *
 * The four questions a column of codes asks are not the four a column of names
 * asks — a code column's fourth is leading zeros and a name column's is
 * Inc/LLC/Ltd/Corp — so the switch set is keyed by value kind rather than being one
 * fixed four. This is the single declaration of those ids: the screen renders them
 * and `normalizeMatchLadder` states every one of them on the wire, and a second
 * list somewhere else is how a switch an operator turned off silently comes back on.
 */
export const lookupSpellingSwitchIds = {
  name: ['case', 'punctuation', 'spaces', 'legal-suffix'],
  code: ['case', 'separators', 'spaces', 'leading-zeros'],
} as const;

/**
 * Which of the spelled-the-same-way switches are on, keyed by the ids above.
 *
 * A map rather than named booleans because the ids differ by value kind; a flat
 * `ignoreLegalSuffixes` would be a field that means nothing on half the rules.
 */
export type SpellingSwitches = Record<string, boolean>;

/**
 * Which rungs of the ladder are enabled, how the first rung spells things, and the
 * two guards the close-spelling rung answers to.
 *
 * `normalisation` is the "spelled the same way" rung stated as settings rather than
 * printed as a label. A job that must keep *Bright Star LLC* apart from *Bright
 * Star Ltd* turns `legal-suffix` off, which the shipped screen described and
 * offered no way to do.
 *
 * It is optional here only so that a screen mid-rebuild compiles; it is not
 * optional on the wire. `normalizeMatchLadder` in `shared/lookup-rules.ts` states
 * every switch for the rule's value kind before anything is sent, so the engine
 * never falls back on its own record defaults for a switch the screen is claiming
 * to own.
 */
export type MatchLadderSettings = {
  exact: boolean;
  characterConfusion: boolean;
  fuzzy: boolean;
  /** How alike the best row must be. */
  minimumScore: number;
  /** How far clear of the next-best row it must be. */
  runnerUpMargin: number;
  normalisation?: SpellingSwitches;
};

/**
 * One column of the matched row, and the field it fills.
 *
 * `fieldName` is what `docs/planning/lookups-implementation.md` binds the contract
 * to and is required on the wire; `fieldId` rides along when the screen knows it,
 * because a field can be renamed and the engine already resolves a lookup's source
 * field by identity before falling back to name.
 */
export type LookupReturn = { column: string; fieldName: string; fieldId?: string | null };

/**
 * A return as it may arrive from a renderer, before the boundary has checked it.
 *
 * Everything crossing from the renderer is untrusted input, so the inbound shape is
 * deliberately looser than the outbound one. `canonicalLookupRule` turns this into
 * `LookupReturn` or refuses with a sentence naming the rule — a return carrying a
 * field id and no field name is refused, not guessed at.
 */
export type LookupReturnDraft = { column: string; fieldName?: string; fieldId?: string | null };

export type LookupRuleDefinition = {
  id: string;
  jobId: string;
  name: string;
  dataTableId: string;
  findColumn: string;
  /**
   * Every field this rule fills, off the one row it resolved.
   *
   * One match, many fields. Four rules each matching the same reading against the
   * same column can disagree — one with the close-spelling rung on and one without,
   * or a table re-imported between two of them — and then one invoice takes its
   * number from row 3 and its terms from row 7, silently. Optional in the type only
   * while the screen is being rebuilt; a rule that reaches the main process with
   * none is refused by name rather than defaulted.
   */
  returns?: LookupReturnDraft[];
  valueKind: string;
  matchLadder: MatchLadderSettings;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * The question the screen asks before anything is tested: can a value out of this
 * field ever be a value in that column?
 *
 * This is the failure the rebuild exists for — a vendor *number* typed at a
 * *name*-matching rule, with nothing on screen to warn of it. It is answerable
 * from the two columns alone, so no rule need exist and no match is attempted.
 */
export type LookupFitRequest = {
  jobId: string;
  dataTableId: string;
  /** The column in the list that would be matched against. */
  column: string;
  /**
   * The field whose readings would be matched, by the name the job gives it.
   *
   * A name and not a stable id because `EngineRequest.LookupSourceFieldName` is the
   * spelling the engine declares, and one side inventing the other spelling on its
   * own is how a request is sent and never read. A renamed field therefore has to
   * be asked about under its new name; if that proves to matter, the fix is a
   * stable id on both sides at once, not a second spelling on one.
   */
  sourceFieldName?: string;
  /** Readings to judge the field by, when it has none stored yet. */
  sourceValues?: string[];
};

/** The answer, in the words the wire between the page and the list is drawn with. */
export type LookupFitVerdict = {
  /** `different-shape` is the one that stops the wire between the document and the list. */
  fit: 'same-shape' | 'different-shape' | 'unknown';
  /** One sentence, ready to print beside the stopped wire. */
  headline: string;
  /** Why, in terms of what each side actually holds. */
  detail: string;
  sourceValueKind: string;
  columnValueKind: string;
  sourceExamples: string[];
  columnExamples: string[];
  /** How many readings the source side was judged from. Zero means the kind alone answered. */
  readingCount: number;
};

/** What one rung fills and what it costs, measured rather than typed. */
export type LookupRungCost = {
  rung: 'exact' | 'character-confusion' | 'fuzzy';
  enabled: boolean;
  filled: number;
  outOf: number;
  wrongRows: number;
  /** The measurement in the wording the rung is labelled with — "fills 31 of 40 · no wrong rows". */
  sentence: string;
};

/**
 * The cost of the rule as it stands, over the field's own recent readings.
 *
 * `measuredFrom` is the honesty of the whole report. `field-readings` is the
 * number the screen claims to be showing; `table-simulation` is the older
 * measurement of the rule against its own table, which is a different number about
 * a different thing and must not be printed under "your last 40 readings".
 */
export type LookupCostReport = {
  ruleId?: string | null;
  sourceFieldName: string;
  measuredFrom: 'field-readings' | 'table-simulation';
  readingCount: number;
  filled: number;
  held: number;
  wrongRows: number;
  rungs: LookupRungCost[];
  notes: string[];
};

/** The answer, and the rung that produced it. Refusals carry a rung too. */
export type LookupMatch = {
  outcome: 'matched' | 'ambiguous' | 'unmatched';
  rung: 'exact' | 'character-confusion' | 'fuzzy' | 'none';
  explanation: string;
  value?: string | null;
  matchedValue?: string | null;
  rowNumber?: number | null;
  score: number;
  runnerUpScore: number;
  candidates?: string[] | null;
  /** The matched row as the rule reads it, one entry per return (the engine's `Returns`). */
  returns?: LookupReturnValue[] | null;
  /** The rows a tie was between, earliest first (the engine's `CandidateRows`). */
  candidateRows?: LookupCandidateRow[] | null;
};

/** What one row writes into one field. */
export type LookupReturnValue = { column: string; fieldName: string; value?: string | null };

/** One of the rows a tied reading could mean, and what it would fill. */
export type LookupCandidateRow = {
  rowNumber: number;
  matchedValue?: string | null;
  returns?: LookupReturnValue[] | null;
};

/** What review shows beside a field a lookup populated. */
export type LookupEvidence = {
  ruleId: string;
  ruleName: string;
  dataTableId: string;
  dataTableName: string;
  sourceFieldId: string;
  sourceValue: string;
  rung: string;
  explanation: string;
  rowNumber?: number | null;
  matchedValue?: string | null;
  /** The column of the matched row this field was filled from. */
  returnColumn?: string | null;
  /** The row matched and this column is empty on it. */
  blankOnMatchedRow?: boolean;
  /** When the reading fit more than one row: what each of those rows would write into this field. */
  choices?: LookupChoice[] | null;
};

/** What one tied row would write into one field. */
export type LookupChoice = { rowNumber: number; value?: string | null };

export type LookupValidationRungReport = {
  rung: string;
  recovered: number;
  wrongMatch: number;
  refused: number;
  recoveryRate: number;
  wrongMatchRate: number;
};

/** The rule measured against its own table, with no customer documents involved. */
export type LookupValidationReport = {
  dataTableId: string;
  findColumn: string;
  returnColumn: string;
  valueKind: string;
  sampledValueCount: number;
  simulatedReadingCount: number;
  alteredReadingCount: number;
  seed: number;
  rungs: LookupValidationRungReport[];
  notes: string[];
  sentences?: string[] | null;
};

export type JobDocumentTypeDefinition = {
  id: string;
  name: string;
  semanticId?: string | null;
  description?: string | null;
  status: 'provisional' | 'active' | 'archived';
  schemaVersion: number;
  position: number;
  fields: JobFieldDefinition[];
};

/** Support access (ADR-0088). `expiresAt` is an ISO instant while on, null while off. */
export type SupportAccessState = { on: boolean; expiresAt: string | null };

/** The folder a job watches for arriving files (ADR-0090 decision 10). One a job; a folder serves one job. */
export type WatchedFolderSetting = {
  jobId: string;
  path: string;
  enabled: boolean;
  addedAt: string;
  /** What arrives in the folder (ADR-0091): scan stacks (the default) or one document per file. */
  separation?: 'whole' | 'stack';
};
export type WatchedFolderCheck = { value: boolean; message: string };
/** What the coordinator, as the identity it runs under, can do in a folder: each answer with a sentence for a person. */
export type WatchedFolderTestResult = {
  path: string;
  /** Everything a watched folder needs is true. */
  usable: boolean;
  /** The one sentence to show. */
  summary: string;
  exists: WatchedFolderCheck;
  canList: WatchedFolderCheck;
  canCreate: WatchedFolderCheck;
  canRename: WatchedFolderCheck;
  canDelete: WatchedFolderCheck;
  sameVolume: WatchedFolderCheck;
  networkShare: WatchedFolderCheck;
  /** From the folder's permissions: it may move files OTHER accounts put there (a scanner, a person), not only its own test file. */
  canTakeOthersFiles: WatchedFolderCheck;
  /** The only thing wrong is canTakeOthersFiles, so an administrator's grant on this folder is what would make it usable. */
  grantable: boolean;
};
export type WatchedFolderStatus = WatchedFolderSetting & {
  /** watching; waiting (it will try again by itself); unavailable (the folder cannot be used); stopped; disabled. */
  state: 'watching' | 'waiting' | 'unavailable' | 'stopped' | 'disabled';
  /** Why it is waiting or unavailable, in words for a person. */
  reason: string | null;
  since: string | null;
  /** Files seen and not yet in the job. */
  waitingFiles: number;
  takenToday: number;
  lastTaken: { name: string; at: string } | null;
  /** The last file that was not a document (moved to Unprocessable in the folder, with its reason). */
  lastFailure: { name: string; at: string; reason: string } | null;
  stillBeingWritten: { files: number; oldestName: string; since: string } | null;
  /** Empty (0-byte) files that stayed empty for ten minutes: never taken. Absent when none. */
  emptyFiles?: number;
  /** Files Windows will not let Paige move out of the folder (access denied), with the fix. Absent when none. */
  cannotTake?: { files: number; oldestName: string; since: string; reason: string };
};
/** Job setup › Watched folder: what the status does not carry. */
export type WatchedFolderDetails = {
  /** Who takes the files: the Windows service (always), or this person's Paige (while it is open). */
  runsAs: 'service' | 'application';
  /** The files in the folder's `Unprocessable`, newest first (at most 20), each with the first line of its reason. */
  setAside: {
    folder: string | null;
    count: number;
    files: { name: string; reason: string | null; at: string }[];
  };
};
/** One job's work with nobody at the computer. Counts are null when the engine could not be asked. */
export type UnattendedJobStatus = {
  jobId: string;
  jobName: string;
  watching: WatchedFolderStatus | null;
  filesWaiting: number;
  captures: {
    queued: number;
    processing: number;
    /** Waiting for a person at a window (kept pages of a stopped scan, a job not set up yet). */
    held: number;
    /** Read and failed; tried again by itself after 1, 5, 15, then every 60 minutes. */
    failing: number;
    /** Not started yet for a reason that passes by itself. */
    waiting: number;
    nextRetryAt: string | null;
    lastError: string | null;
    heldReasons: string[];
  };
  /** Why nothing of this job starts now (paused, the license, an update, a speed check, a window at work), or null. */
  blocked: { kind: string; reason: string } | null;
  documentsWaitingForReview: number | null;
  documentsWaitingToExport: number | null;
  exportWaitReasons: string[];
  lastExported: { name: string | null; at: string } | null;
  /** Plain sentences; empty when healthy. */
  needsAttention: string[];
};
export type UnattendedStatus = {
  generatedAt: string;
  queueRunning: boolean;
  /** After several failures in a row the whole queue rests until this time. */
  queueRestingUntil: string | null;
  /** Files taken from a watched folder that could not be given to a job; kept, never deleted. */
  orphanedFiles: number;
  jobs: UnattendedJobStatus[];
  /** Every job's sentences, each led by its job's name; empty when healthy. */
  needsAttention: string[];
  notes: string[];
  /** Paige is being moved to the Windows service: no new work and no update until it closes, or until `until`. */
  workHold?: { reason: string; until: string } | null;
};
/** What changed outside the window, so it re-reads what its own path would have (support-window-bridge.ts). */
export type JobsChangedEvent = {
  jobId: string;
  change: 'created' | 'updated' | 'removed' | 'documents' | 'export-settings';
};
/** A support request the window runs through its own handlers (support-window-bridge.ts). */
export type SupportWindowCommandEvent = {
  requestId: string;
  command: string;
  args: Record<string, unknown>;
};
export type SupportWindowCommandReply = {
  requestId: string;
  ok: boolean;
  result?: unknown;
  error?: string;
};
export type JobProcessingState = {
  jobId: string;
  pauseRequested: boolean;
  activeOperations: number;
  revision: number;
  status: 'ready' | 'running' | 'queued' | 'pausing' | 'paused';
};

export type LearningResetRequest = {
  typeId: string;
  scope: 'field' | 'document-type' | 'line-items';
  fieldId?: string | null;
  expectedSchemaVersion: number;
  confirmed: boolean;
};
export type LearningResetResult = { resetId: string; retiredAnswers: number; job: JobDefinition };

/**
 * What removing one document type from a job will do, counted by the engine from the
 * same selection the removal runs (DocumentStore.TypeRemoval). `blockedReason` is a
 * sentence for the operator when it cannot run now.
 */
export type DocumentTypeRemovalImpact = {
  typeId: string;
  typeName: string;
  expectedSchemaVersion: number;
  /** Unexported documents of the type: they lose the type. */
  documentsToReview: number;
  /**
   * Of those, the ones the automatic re-read reads again among the remaining types with the normal type question
   * (D9, ADR-0096): all of them but those with a page never read (they wait for Read it again), and none when the
   * type is the job's last. Each is filed under a type that fits, or left with no type: Manual then holds it for a
   * person, Autopilot sends it to the exceptions folder. Absent from an engine that predates it.
   */
  documentsToRepick?: number;
  /**
   * Of `documentsToRepick`, those with a value a person confirmed: one the type found has no field for keeps the
   * document with no type, with its answers (Manual holds it; Autopilot sends it to exceptions). Absent from an
   * engine that predates it.
   */
  documentsWithConfirmedValues?: number;
  /** Exported documents of the type: left exactly as they are. */
  documentsExported: number;
  /** Documents whose confirmed answers teach the type; that teaching is forgotten. */
  confirmedDocuments: number;
  lastType: boolean;
  blocked: boolean;
  blockedReason?: string | null;
};
export type DocumentTypeRemovalRequest = {
  typeId: string;
  expectedSchemaVersion: number;
  confirmed: boolean;
};
export type DocumentTypeRemovalResult = {
  job: JobDefinition;
  removed: DocumentTypeRemovalImpact;
  /** A very large type: documents still being moved to review in the background when this returned. */
  documentsStillMoving?: number;
};
/** A type the operator removed, so it cannot come back without them deciding (a stale save, a stored setup proposal). */
export type RemovedDocumentType = { id: string; name: string; removedAt: string };
/** A name one of the job's types has carried, now or in a saved version: how Review knows a renamed type's older documents. */
export type DocumentTypeName = { id: string; name: string };

export type JobDefinition = {
  id: string;
  name: string;
  description: string;
  accent: string;
  bootstrapMode: 'scan' | 'folder' | 'sample-scan' | 'manual' | 'import' | 'content-central';
  status: 'provisional' | 'active' | 'archived';
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  documentTypes: JobDocumentTypeDefinition[];
  // Derived from SEPARATION rather than restating it. A fourth mode added to the
  // constants and not to a hand-written union is a value the engine accepts and
  // this side refuses to type.
  separationMode: (typeof SEPARATION)[keyof typeof SEPARATION];
  separationBarcodeText?: string | null;
  boundaryOpeningProfiles?: BoundaryOpeningProfile[];
  boundaryContinuationProfiles?: BoundaryContinuationProfile[];
  suppressedSuggestions?: string[];
  suggestionsPausedUntil?: string | null;
  reviewPolicy?: ReviewPolicy | null;
  learningStance?: LearningStance | null;
  /** Engine-owned: saving a job never changes it; adding the type back (by hand or from a suggestion) lifts it. */
  removedDocumentTypes?: RemovedDocumentType[] | null;
};
/**
 * What this job has been told to stop proposing on its own — a document type it
 * does not have, a field it does not collect. Timestamps rather than booleans:
 * "since when" is what the control prints and what an operator asks a month
 * later. Absent means Paige suggests both, which is every job until
 * somebody says otherwise. A stance never changes what lands in review.
 */
export type LearningStance = {
  newTypesStoppedAt?: string | null;
  newFieldsStoppedAt?: string | null;
  offerDeclinedAt?: string | null;
  /**
   * Calibration (owner, 2026-10-30; shared/calibration.ts): on since this time. A new job starts with it;
   * jobs made before it existed have none and are not calibrating.
   */
  calibrationStartedAt?: string | null;
  /** When Calibration went off, and by whom: the operator, or Paige once the job was ready. */
  calibrationEndedAt?: string | null;
  calibrationEndedBy?: CalibrationEndedBy | null;
};
export type CalibrationEndedBy = 'operator' | 'ready';
export type LegacyInstallation = {
  databaseName: string;
  jobs: LegacyJobSummary[];
  connectionSource?: string | null;
  connections?: { id: string; label: string; databaseName: string; server: string }[] | null;
};
export type LegacyJobSummary = {
  id: string;
  name: string;
  pageCount: number;
  trainingImageCount: number;
  documentTypes: { id: string; name: string; fields: string[] }[];
};
export type LegacyImportRequest = {
  sourceJobId: string;
  importId: string;
  recentPages: number;
  connectionSource?: string | null;
};
export type LegacyImportStatus = {
  job: JobDefinition;
  state: string;
  trainingImages: number;
  recentImages: number;
  analyzedImages: number;
  warnings: string[];
  error: string | null;
  sourceJobId: string;
  requestedRecentPages: number;
  typeTeachingAdmitted: boolean;
  teachingDecision: string | null;
  connectionSource?: string | null;
};
/**
 * How sure Paige must be, per area, before a document passes without a
 * look. Fractions 0..1. Hard problems — a required value missing, totals that
 * do not add up, a value with no evidence on the page — always flag and no
 * threshold here silences them. Absent means the engine's measured defaults.
 */
export type ReviewPolicy = {
  documentType: number;
  fields: number;
  boundary: number;
  holdForReview?: boolean;
  documentTypeEnabled?: boolean;
  fieldsEnabled?: boolean;
  boundaryEnabled?: boolean;
  lineItemArithmeticEnabled?: boolean;
  /**
   * The pilot mode: who finishes a document that needs a check. "human-reviewed" is Manual (a person; also what
   * absent means); "ai-reviewed" is Autopilot (the AI reviewer where this computer can run it, unchecked where it
   * cannot). "straight-through", the Autopilot before 2026-10-19, is still read, as Autopilot; nothing saves it.
   * It is also the mode in force the engine reports (ReviewReleasePreview.mode) where Autopilot runs without the AI check.
   */
  processingMode?: ProcessingMode;
};
export type ProcessingMode = 'straight-through' | 'ai-reviewed' | 'human-reviewed';
/** `startsOnlyOnOpenings`: the job's documents start only on pages like its openings (ADR-0102); kept as the engine sent it. */
export type BoundaryOpeningProfile = {
  id: string;
  openingTerms: string[];
  supportCount: number;
  startsOnlyOnOpenings?: boolean;
};
/** The mirror of an opening profile: a page shape corrections say is NOT a start. `pair` is set for a page that
 * continues only after a certain page; its own terms are then empty on purpose (engine: BoundaryContinuationPair). */
export type BoundaryContinuationProfile = {
  id: string;
  continuationTerms: string[];
  supportCount: number;
  pair?: { pageTerms: string[]; precededByTerms: string[] };
};
export type JobDeletionRequest = { jobId: string; confirmationName: string };

/**
 * A job's setup written to one file: document types and fields, lookups and the
 * tables they read INCLUDING THEIR ROWS, how pages are separated, and the review bars.
 *
 * The file carries its own account of itself. `includes` is what travelled and
 * `excludes` is what did not, both written by the engine from the one allow-list the
 * export is built from, so the two lists cannot drift from the file beside them.
 */
export type JobTemplateExportResult = {
  filePath: string;
  jobName: string;
  appVersion: string;
  exportedAt: string;
  templateSchemaVersion: number;
  documentTypeCount: number;
  fieldCount: number;
  lookupRuleCount: number;
  dataTableCount: number;
  dataTableRowCount: number;
  includes: string[];
  excludes: string[];
  /** Learned page shapes carried, mixed ones included — see mixedLayoutCount. */
  layoutCount: number;
  /** Of those, the shapes that hold more than one kind of document: carried without their maps. */
  mixedLayoutCount: number;
  fieldMapCount: number;
  tableMapCount: number;
  /** Post-model numeric-role maps; never included in tableMapCount/model-skip claims. */
  numericMapCount: number;
  boundaryProfileCount: number;
  /** What the export could not carry and why, in sentences meant for the operator. */
  warnings: string[];
};

/** The new job an imported template stood up, and anything the import had to decide. */
export type JobTemplateImportResult = {
  job: JobDefinition;
  originJobName: string;
  /** True when the template's name was taken here and this job got a numbered one. */
  nameChanged: boolean;
  documentTypeCount: number;
  fieldCount: number;
  lookupRuleCount: number;
  dataTableCount: number;
  dataTableRowCount: number;
  notes: string[];
  layoutCount: number;
  fieldMapCount: number;
  tableMapCount: number;
  numericMapCount: number;
  boundaryProfileCount: number;
  /** Layout reader fine-tunes in force from the first document (ADR-0086). */
  layoutReaderModelCount?: number;
  /** Fine-tunes still waiting for their sample reader download; the base reader reads those types until it lands. */
  pendingLayoutReaderModelCount?: number;
};
/**
 * A job setup file applied to a job that already exists (engine `apply-job-template`, support's
 * apply-job-setup): what came in, and in sentences what was replaced and what was kept.
 */
export type JobTemplateApplyResult = {
  job: JobDefinition;
  originJobName: string;
  documentTypesAdded: number;
  fieldsAdded: number;
  fieldsUpdated: number;
  lookupRuleCount: number;
  dataTableCount: number;
  dataTableRowCount: number;
  layoutCount: number;
  fieldMapCount: number;
  tableMapCount: number;
  numericMapCount: number;
  boundaryProfilesAdded: number;
  fieldRuleSets: number;
  tableRuleSets: number;
  layoutReaderModelCount: number;
  pendingLayoutReaderModelCount: number;
  fastReaderFields: number;
  choiceFields: number;
  replaced: string[];
  kept: string[];
  notes: string[];
};

/** A job setup file being written or read: sent once the file is chosen, so the screen shows
 * the work from then until the call answers, and nothing while the picker is open. */
export type JobSetupFileWork = { action: 'download' | 'import' };

/**
 * A sample job's reader weights downloading while the operator waits to open it (owner decision
 * 2026-09-30). `done` closes the progress screen whether the weights arrived or not.
 */
export type SampleReaderDownloadProgress = {
  corpus: string;
  state: 'downloading' | 'done';
  receivedBytes: number;
  totalBytes: number;
};

export type BarcodeResult = {
  value: string;
  symbology: string;
  confidence: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PageResult = {
  number: number;
  previewPath: string;
  /** The original file that physically contributed this page. */
  sourcePath?: string | null;
  /** One-based page/frame number inside `sourcePath`. */
  sourcePageNumber?: number;
  /** Stable identity of this physical import when the same path is acquired again. */
  sourceOccurrenceId?: string | null;
  text: string;
  ocrConfidence: number;
  ocrElapsedMilliseconds: number;
  barcodeElapsedMilliseconds?: number;
  lines: OcrLine[];
  words: OcrWord[];
  barcodes: BarcodeResult[];
  /**
   * The reserved value of the break sheet that opened this page's document, on its
   * first page only. The sheet itself is never a page of the document.
   */
  breakSheet?: string;
  startsNewDocument?: boolean;
  boundaryConfidence?: number;
  boundaryReasons?: string[];
  /** Whether this page's source separates documents itself (ADR-0091): "whole", "stack" or "unknown". */
  sourceSeparation?: SourceSeparation | null;
  /** The source kind its file came from ("import", "scanner", "watched-folder", "legacy"). */
  sourceKind?: string | null;
  /**
   * Engine only: where the source policy overruled the automatic detector. True on a page inside a file
   * kept whole that the detector would have started a document on (the split suggestion).
   */
  detectedStart?: boolean | null;
  /** Engine only (final review B2): the detector answered here without the boundary model (its wave was all kept whole). */
  detectedByRulesOnly?: boolean;
  /** Degrees this page was rotated before it was read, positive clockwise. */
  deskewDegrees?: number;
  /** The quarter turn this page's image carries, clockwise from the page as scanned: 0, 90, 180 or 270. */
  rotationDegrees?: number;
  /** A person turned this page with Turn page and left it at `rotationDegrees`. */
  turnedByOperator?: boolean;
  /**
   * Why the recogniser did not read this page, when it did not.
   *
   * Absent means a recogniser answered for it — well or badly. Present means the
   * empty text on it is a hole rather than a blank sheet: nothing looked at this
   * page, and neither its emptiness nor the document around it may be scored,
   * exported, or learned from as a reading. The document carries a
   * `page_not_read` finding naming it, and only reading the page again clears it.
   */
  notReadReason?: string | null;
  /** How many readings were attempted for this page. Only meaningful beside `notReadReason`. */
  readAttempts?: number;
  pixelWidth?: number;
  pixelHeight?: number;
  dpiX?: number;
  dpiY?: number;
};

export type FilingToken = {
  kind: 'literal' | 'document-type' | 'field';
  literal?: string | null;
  fieldId?: string | null;
  format?: string | null;
  fallback?: string | null;
};
export type FilingExpression = { parts: FilingToken[]; separator: string };
export type DocumentFilingRule = {
  documentTypeId: string;
  folderSegments: FilingExpression[];
  preferredBaseName: FilingExpression;
};
export type FilingPlan = { schemaVersion: 1; version: number; rules: DocumentFilingRule[] };
export type FilingAdvisorResult = {
  plan: FilingPlan;
  selection: {
    schemaVersion: 2;
    rules: Array<{
      documentTypeId: string;
      folderParts: Array<{ kind: 'document-type' | 'field'; fieldId?: string; format?: string }>;
      fileNameParts: Array<{ kind: 'document-type' | 'field'; fieldId?: string; format?: string }>;
    }>;
  };
  model: string;
  promptContract: string;
  repaired: boolean;
};
export type ExportCapabilities = { pdfA2u: boolean };
export type ExportJobSettings = {
  jobId: string;
  destinationRoot: string;
  triggerMode: 'manual' | 'continuous' | 'scheduled';
  pdfProfile: 'searchable-pdf' | 'pdfa-2u';
  retentionDays: 30 | 60 | 90 | 180 | 365 | null;
  filingPlan: FilingPlan;
  updatedAt: string;
  scheduleLocalTime?: string | null;
  scheduleTimeZone?: string | null;
  enabled: boolean;
  localStoreRoot?: string | null;
  destinationKind?: 'filesystem' | 'content-central';
  destinationBindingId?: string | null;
  /**
   * Why the last automatic export run ("as soon as ready", "once a day") could not start,
   * in the engine's own sentence; absent once a run starts again. Read-only.
   */
  automaticHold?: { code: string; message: string; heldAtUtc: string } | null;
  /**
   * The job's exceptions folder (ADR-0096): where Autopilot sends the documents it can't file as one of the
   * job's types, always a local folder. Filled in by the main process when the job has none
   * (`<Documents>\Paige Jobs\<job>\exceptions`, or `%ProgramData%\Ademero\Paige Jobs\<job>\exceptions`
   * under the service), saved once, never cleared. Read-only here: a change is a
   * chosen folder's `exceptionsToken`.
   */
  exceptionsRoot?: string | null;
  /** A folder export's files: the PDF with its data and text files (the default), or the PDF alone. */
  files?: ExportFiles;
};
export type ExportFiles = 'pdf-data-text' | 'pdf-only';
export type ExportDestinationSelection = { token: string; displayPath: string };
/**
 * The real default folders of a job's export settings, worked out the way they are made (`prepareJobFolder`'s dry
 * run in the jobs folder: the same "(2)", "(3)" rule, nothing made). `exceptionsRoot` is null when that dry run fails.
 * `backupCopiesRoot` is `<data root>\backup-copies`.
 */
export type ExportFolderDefaults = { exceptionsRoot: string | null; backupCopiesRoot: string };
export type ExportSettingsSaveRequest = Omit<
  ExportJobSettings,
  'destinationRoot' | 'localStoreRoot' | 'updatedAt' | 'automaticHold' | 'exceptionsRoot'
> & {
  destinationToken?: string;
  /** A folder chosen for the exceptions folder (chooseExceptionsFolder); without it the saved one, or the default, is kept. */
  exceptionsToken?: string;
};
export type DestinationConnectionSummary = {
  connectionId: string;
  kind: 'content-central';
  displayName: string;
  baseUrl: string;
  serverInstanceId: string;
  protocolVersion: string;
  redactedKeyPrefix: string;
  capabilities: string[];
  lastSuccessAt?: string | null;
  lastFailureCode?: string | null;
  /** Plain-language kind of the last failure: unreachable, certificate, key-refused, timeout or other. */
  lastFailureCategory?: ContentCentralFailureCategory | null;
  /** The operator sentence for the last failure; shown first. */
  lastFailureMessage?: string | null;
  /** The exact technical error behind the last failure; shown under Technical details. */
  lastFailureDetail?: string | null;
  createdAt: string;
  updatedAt: string;
};
export type ContentCentralFailureCategory =
  'unreachable' | 'certificate' | 'key-refused' | 'timeout' | 'other';
export type ContentCentralConnectionRequest = { displayName: string; baseUrl: string };
export type ContentCentralCatalogSummary = {
  id: string;
  name: string;
  description?: string | null;
  documentTypeCount: number;
  canAddDocuments: boolean;
  schemaRevision?: string | null;
  filingRevision?: string | null;
};
export type ContentCentralCatalogPage = {
  items: ContentCentralCatalogSummary[];
  nextCursor?: string | null;
};
export type ContentCentralFieldDefinition = {
  id: string;
  name: string;
  valueType: 'text' | 'date' | 'decimal' | 'integer';
  required: boolean;
  multiple: boolean;
  valueOwner: string;
  maximumLength?: number | null;
  decimalPlaces?: number | null;
  choices: string[];
  lineItemGroupId?: string | null;
  lineItemOrder?: number | null;
  hidden: boolean;
  extractable: boolean;
  warnings: string[];
};
export type ContentCentralFilingItem = {
  kind: 'new-folder' | 'literal' | 'field' | 'document-type';
  fieldId?: string | null;
  literal?: string | null;
  datePart?: string | null;
  maximumCharacters?: number | null;
};
export type ContentCentralDocumentTypeDefinition = {
  id: string;
  name: string;
  canAddDocuments: boolean;
  fields: ContentCentralFieldDefinition[];
  filing: {
    folderBuildingEnabled: boolean;
    fileBuildingEnabled: boolean;
    emptyFieldText: string;
    invalidCharacterReplacement: string;
    folderItems: ContentCentralFilingItem[];
    fileItems: ContentCentralFilingItem[];
  };
  warnings: string[];
};
export type ContentCentralCatalogDefinition = {
  catalog: ContentCentralCatalogSummary;
  documentTypes: ContentCentralDocumentTypeDefinition[];
};
export type CatalogBackedJobResult = {
  job: JobDefinition;
  binding: JobDestinationBinding;
  warnings: string[];
};
export type BoundCatalogJobSummary = { jobId: string; jobName: string };
export type ContentCentralFilingPreview = {
  externalDocumentTypeId: string;
  logicalFolders: string[];
  filename: string;
  diagnostics: string[];
};
export type ContentCentralFilingPreviewResult = {
  catalogId: string;
  schemaRevision: string;
  filingRevision: string;
  previews: ContentCentralFilingPreview[];
};
export type ContentCentralProvisioningPlan = {
  planId: string;
  proposalFingerprint: string;
  planFingerprint: string;
  targetSchemaRevision?: string | null;
  expiresAtUtc: string;
  changes: Array<{
    objectKind: string;
    action: string;
    externalId: string;
    remoteId?: string | null;
    displayName: string;
    details?: Record<string, unknown> | null;
  }>;
  blockers: string[];
  warnings: string[];
  previews: ContentCentralFilingPreview[];
};
export type PendingContentCentralProposal = {
  connectionId: string;
  plan: ContentCentralProvisioningPlan;
  applyOperationId?: string | null;
  applyState?: string | null;
  applyIdempotencyKey?: string | null;
  applyOutcomeKnown?: boolean;
  applyErrorCode?: string | null;
  applyErrorMessage?: string | null;
};
export type PendingContentCentralProposalDecision = {
  jobId: string;
  connectionId: string;
  planId: string;
  planFingerprint: string;
  applyIdempotencyKey?: string | null;
  applyState?: string | null;
  applyOperationId?: string | null;
};
export type ExportConfigurationResult = {
  settings: ExportJobSettings;
  binding: JobDestinationBinding;
};
export type ContentCentralApplyCommitProof = {
  jobId: string;
  connectionId: string;
  planId: string;
  planFingerprint: string;
  applyIdempotencyKey: string;
  applyOperationId: string;
  bindingId: string;
  bindingRevision: number;
  catalogId: string;
};
export type DestinationFieldBinding = {
  localDocumentTypeId: string;
  localFieldId: string;
  remoteFieldId: string;
  remoteValueType: string;
  valueOwnership: string;
  isLineItem: boolean;
  lineItemOrder?: number | null;
  required?: boolean;
  multiple?: boolean;
  maximumLength?: number | null;
  decimalPlaces?: number | null;
  choices?: string[] | null;
  deliverValue?: boolean;
};
export type DestinationTypeBinding = {
  localDocumentTypeId: string;
  remoteDocumentTypeId: string;
  remoteName: string;
  fields: DestinationFieldBinding[];
  unsentLocalFieldIds?: string[] | null;
  /** Why this type's documents currently wait (the last preflight or check found drift); null when it can deliver. */
  holdReason?: string | null;
};
export type ExportReadinessPreview = {
  considered: number;
  eligible: number;
  blockers: string[];
  /** Documents held only because their document type's Content Central mapping needs review. */
  destinationHeld?: number;
  /** The same count per local document type id. */
  destinationHeldByType?: Record<string, number>;
  /** Of `eligible`, the documents that leave for the exceptions folder instead of the destination (ADR-0096). */
  toExceptions?: number;
  exceptionDocumentIds?: string[];
};
export type JobDestinationBinding = {
  bindingId: string;
  jobId: string;
  destinationKind: 'filesystem' | 'content-central';
  connectionId?: string | null;
  remoteCatalogId?: string | null;
  remoteCatalogName?: string | null;
  serverInstanceId?: string | null;
  schemaAuthority: 'capturePoint' | 'destination';
  filingAuthority: 'capturePoint' | 'destination';
  remoteDeliverySchemaRevision?: string | null;
  remoteFilingRevisionObserved?: string | null;
  bindingRevision: number;
  status: string;
  documentTypes: DestinationTypeBinding[];
  createdAt: string;
  updatedAt: string;
};
export type ContentCentralMappingDecision = {
  objectKind: 'document-type' | 'field';
  localId: string;
  localName: string;
  classification: 'exact' | 'conflict' | 'unmapped' | string;
  remoteId?: string | null;
  remoteName?: string | null;
  message?: string | null;
  candidates?: Array<{ remoteId: string; remoteName: string; compatibility: string }> | null;
  parentLocalDocumentTypeId?: string | null;
};
export type ContentCentralMappingChoice = {
  localDocumentTypeId: string;
  remoteDocumentTypeId: string;
  fields: Record<string, string | null>;
};
export type ContentCentralBindingProposal = {
  binding?: JobDestinationBinding | null;
  blockers: string[];
  plan?: ContentCentralProvisioningPlan | null;
  mappings?: ContentCentralMappingDecision[] | null;
};
export type FilingDiagnostic = { code: string; message: string; severity: string };
export type FilingPlanPreviewItem = {
  documentId: string;
  documentType: string;
  folders: string[];
  preferredBaseName: string;
  diagnostics: FilingDiagnostic[];
};
export type FilingPlanPreview = {
  documents: number;
  valid: number;
  invalid: number;
  exactCollisions: number;
  examples: FilingPlanPreviewItem[];
  diagnostics: FilingDiagnostic[];
};
export type ExportArtifact = {
  role: string;
  fileName: string;
  mediaType: string;
  bytes: number;
  sha256: string;
  profile?: string | null;
};
/** Held-back answers of one layout reader model on one type (ADR-0086). */
export type LayoutReaderHeldBackScore = {
  right: number;
  wrong: number;
  declined: number;
  score: number;
  assuredScore: number;
};
export type LayoutReaderModelVersion = {
  version: string;
  origin: 'learned' | 'carried' | string;
  adoptedAt: string;
  modelSha256: string;
  trainingDocuments: number;
  heldBackDocuments: number;
  before: LayoutReaderHeldBackScore;
  after: LayoutReaderHeldBackScore;
  summary: string;
};
export type LayoutReaderTypeState = {
  documentType: string;
  model: 'base' | 'fine-tuned' | string;
  inForce: LayoutReaderModelVersion | null;
  earlier: LayoutReaderModelVersion[];
  lastVerdict: {
    adopted: boolean;
    reason: string;
    heldBackDocuments: number;
    inForce: LayoutReaderHeldBackScore;
    candidate: LayoutReaderHeldBackScore;
    regressions: number;
  } | null;
};
/** The layout reader's learning for one job, as the Learning screen shows it (engine "layout-reader-models"). */
export type LayoutReaderLearningState = {
  jobId: string;
  readerEnabled: boolean;
  canLearnHere: boolean;
  cannotLearnReason: string | null;
  confirmedDocuments: number;
  nextAttemptAt: number;
  lastAttempt: {
    at: string;
    outcome: string;
    confirmedDocuments: number;
    summary: string;
    device: string | null;
    steps: number;
    trainSeconds: number;
  } | null;
  types: LayoutReaderTypeState[];
  /** The attempt running now, from the engine's memory (never waited on): its phase and step. */
  running?: LayoutReaderRunState | null;
};
export type LayoutReaderRunState = {
  phase: 'checking' | 'training' | 'judging' | 'waiting';
  startedAt: string;
  updatedAt: string;
  trainingDocuments: number;
  step: number;
  steps: number;
  judged: number;
  toJudge: number;
  yields: number;
  lastYieldAt: string | null;
};

export type ExportDocumentSummary = {
  exportId: string;
  jobId: string;
  jobName: string;
  batchId: string;
  documentId: string;
  documentType: string;
  state: string;
  preferredBaseName: string;
  resolvedBaseName: string;
  logicalFolders: string[];
  frozenAtUtc: string;
  verifiedAtUtc?: string | null;
  destinationLocator?: string | null;
  localPackagePath?: string | null;
  purgeEligibleAtUtc?: string | null;
  failureCode?: string | null;
  failureMessage?: string | null;
  artifacts: ExportArtifact[];
  destinationKind?: 'filesystem' | 'content-central';
  remoteDocumentId?: string | null;
  realizedFilename?: string | null;
  realizedLogicalFolders?: string[] | null;
  displayFilename?: string | null;
  remoteIndexingState?: string | null;
  remoteWorkflowState?: string | null;
  remoteEffectState?: string | null;
  localRecoveryState?: string | null;
  remoteOutcomeKnown?: boolean | null;
  remoteCancelable?: boolean | null;
  remoteReturnable?: boolean | null;
  remoteRecoveryAction?:
    'reconcile-cancellation' | 'complete-local-recovery' | 'cancel' | 'retry-delivery' | null;
  maintenanceState?: 'queued' | 'working' | 'held' | 'needs-attention' | null;
  maintenanceKind?: 'staging-cleanup' | 'retention-purge' | null;
  maintenanceMessage?: string | null;
  maintenanceAttemptCount?: number | null;
  maintenanceNextAttemptAtUtc?: string | null;
  /**
   * A document sent to the exceptions folder (ADR-0096): Activity's "Sent to exceptions: <reason sentence>" row.
   * `route` is "exceptions"; absent for exports to the job's destination.
   */
  route?: 'exceptions' | null;
  exceptionReason?: ExceptionReason | null;
  /** The reason in one plain sentence, as the PDF's properties say it. */
  exceptionReasonText?: string | null;
  /** Opens the document in Teach: "job/batch/document". */
  teachId?: string | null;
  /** An exceptions send whose document was deleted since: its Activity row says Deleted instead of offering Delete. */
  documentDeleted?: boolean | null;
};
/** The exceptions route's stable reason codes (ADR-0096); also in the file name and the PDF's properties. */
export type ExceptionReason =
  | 'not-a-document'
  | 'no-type'
  | 'ai-check-not-answered'
  | 'not-read'
  | 'type-not-matched'
  | 'destination-type-changed'
  | 'value-refused';
/** `total` and `activeTotal` follow the request's filters; `needsAttentionTotal` is the whole job's, unfiltered. */
export type ExportDocumentPage = {
  documents: ExportDocumentSummary[];
  offset: number;
  limit: number;
  total: number;
  activeTotal: number;
  needsAttentionTotal: number;
};
export type ExportDocumentListRequest = {
  jobId: string;
  offset?: number;
  query?: string;
  documentType?: string;
  state?: string;
  exportedFromUtc?: string;
  exportedToUtc?: string;
};
export type ExportRunResult = {
  considered: number;
  frozen: number;
  exported: number;
  held: number;
  failed: number;
  blockers: string[];
  documents: ExportDocumentSummary[];
};

export type OcrPoint = { x: number; y: number };
export type OcrBox = { x: number; y: number; width: number; height: number };
export type OcrLine = { text: string; confidence: number; polygon: OcrPoint[] };
/** `box`: the OCR worker's even split of its line (what extraction reads). `measuredBox`: where the word's ink is, for display. */
export type OcrWord = {
  text: string;
  confidence: number;
  lineIndex: number;
  box: OcrBox;
  measuredBox?: OcrBox | null;
};

export type FieldResult = {
  fieldId?: string | null;
  fieldKind?: string | null;
  name: string;
  value: string;
  confidence: number;
  source: string;
  needsReview: boolean;
  evidencePageNumber: number;
  evidenceLineIndexes: number[];
  /** The OCR words (indexes into the evidence page's words) that spell the value; absent on older documents. */
  evidenceWordIndexes?: number[] | null;
  canonicalValue?: string | null;
  validationStatus?: 'unparsed' | 'empty' | 'valid' | 'warning';
  validationMessage?: string | null;
  verified?: boolean;
  barcodeEvidence?: BarcodeResult | null;
  /** Set when a lookup, not the page, produced this value. */
  lookupEvidence?: LookupEvidence | null;
  /** Where a fast-reader value's confidence came from: this job's record of that reader on the field. */
  confidenceBasis?: FieldConfidenceBasis | null;
  peerEvidence?: FieldPeerEvidence | null;
  /** The marked box a checkbox reading chose (source "Checked box"), in page pixels; drawn beside the caption's lines. */
  markEvidence?: OcrBox | null;
};
/**
 * The engine's FastReaderTrackRecord basis: confirmed fast-reader readings of this field
 * (right of answers), those shaped like this value (or unlike every confirmed value, when
 * looksConfirmed is false), and the reader's own score, kept only for reference.
 */
export type FieldConfidenceBasis = {
  right: number;
  answers: number;
  shapeRight: number;
  shapeAnswers: number;
  looksConfirmed?: boolean | null;
  readerScore: number;
};
/**
 * The engine's FieldPeerEvidence: what the job's other documents of the same type say about a
 * machine reading it lowered (form text the reader gave unevenly, or a value printed nowhere on
 * its own pages), and the confidence the reading carried before.
 */
export type FieldPeerEvidence = {
  peers: number;
  printedOn: number;
  sameAnswer: number;
  readerConfidence: number;
  printedHere: boolean;
  /** The reader's record on this field over the job's confirmed documents of the type (right of answers). */
  recordRight?: number;
  recordAnswers?: number;
  /** The longer answer the job's other documents give for the field, printed on this page, when this reading is only its leading words. */
  leadingWordsOf?: string | null;
  /** One of the answers the job's other documents give for the field, when this reading is not written the way they write it. */
  shapeUnlike?: string | null;
  /** The value sits under a printed label other than the one the job's other same-type documents answer the field from. */
  readonly otherBox?: boolean;
  /** That label, as the engine folds it, when known. */
  readonly peerLabel?: string | null;
  /** What the reader had read, when the engine replaced it with the peers' box value or completed its own box. */
  readonly replacedValue?: string | null;
  /** The reading was completed to its whole box (a code printed hard against it); replacedValue holds what was read. */
  readonly ownBoxCompleted?: boolean;
};
export type FieldInterpretation = {
  canonicalValue?: string | null;
  status: 'empty' | 'valid' | 'warning';
  message?: string | null;
};

/**
 * What a document is called before anything has worked out what it is.
 *
 * The engine writes this and the desktop tests for it to tell "still being read"
 * from "read and could not be placed" — two states that must not look alike.
 * Both sides held it as a bare literal, so the agreement rested on spelling.
 */
export const UNCLASSIFIED_DOCUMENT = 'Unclassified document';

/**
 * How a document came by its type. The engine sets these and this side branches
 * on them, so they are part of the contract rather than incidental wording.
 */
export const TYPE_FROM_CONFIRMED_EXAMPLES = 'confirmed-examples';
/** Native DocumentTypeTrustLevel receipt; this is not operator verification. */
export const TYPE_TRUST_AUTHORIZED_LEARNED = 2;
export const TYPE_FROM_BOUNDARY_REEVALUATION = 'boundary re-evaluation';
export const TYPE_FROM_OPERATOR_SPLIT = 'operator boundary correction';
/** The operator removed the type this document had; review asks for a new one. */
export const TYPE_FROM_REMOVED_TYPE = 'document type removed';
/**
 * Its type was removed and the re-read among the remaining types named one a confirmed value of it has no field in
 * (ReviewState.TypeFromRemovedTypeAnswersKept). It stays without a type, its answers kept, for a person.
 */
export const TYPE_FROM_REMOVED_TYPE_ANSWERS_KEPT = 'document type removed, answers kept';
/**
 * Its type was removed and the re-read among the remaining types read it (ReviewState.TypeFromRemovedTypeReread): the
 * type that fitted, unconfirmed, or none. With a type, review asks to check the type and values, saying it was read
 * again after its type was removed; with none, it asks for a type as for any untyped document.
 */
export const TYPE_FROM_REMOVED_TYPE_REREAD = 'document type removed, read again';

/**
 * What an operator did, as this side and the engine agree to name it.
 *
 * These are sent from here and matched on there, and a mismatch fails silently —
 * the event is stored, nothing recognises it, no label is written. That is how
 * every verified document start went missing for a night.
 */
export const CORRECTION = {
  fieldValueCorrected: 'field-value-correction',
  fieldValueVerified: 'field-value-verified',
  documentTypeCorrected: 'document-type-correction',
  documentTypeVerified: 'document-type-verified',
  documentConfirmed: 'document-confirmed',
  boundarySplit: 'boundary-split',
  boundaryMerge: 'boundary-merge',
  boundaryUndo: 'boundary-undo',
  boundaryVerified: 'boundary-verified',
  lineItemCorrected: 'line-item-correction',
  tableConfirmed: 'table-confirmed',
  lineItemAdded: 'line-item-added',
  lineItemDeleted: 'line-item-deleted',
  pageRemoved: 'page-removed',
  pageRestored: 'page-restored',
  /** Turn page. Written by the engine operation that turns and re-reads the page, never sent from here. */
  pageTurned: 'page-turned',
} as const;

export type LineItemRow = {
  rowNumber: number;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  confidence: number;
  arithmeticValidated: boolean;
  pageNumber: number;
  evidenceLineIndexes: number[];
  /** The printed OCR cells that support each editable value. */
  cellEvidence?: {
    pageNumber: number;
    descriptionLineIndexes: number[];
    quantityLineIndexes: number[];
    unitPriceLineIndexes: number[];
    amountLineIndexes: number[];
  } | null;
  /** Explicit approval, a completed user-authored row save, or whole-document approval. */
  verified?: boolean;
  /** Reading route or operator-entry for a user-added row; never display copy. */
  source?: string | null;
  /** The operator typed into this row: the engine's standing repairs never rewrite it (not an approval). */
  operatorTyped?: boolean;
};

/** `pageNumbers`: the pages a finding names, when the screen offers an action on them (`page_rotation_guessed`: Turn it). */
export type ValidationFinding = {
  code: string;
  severity: string;
  message: string;
  pageNumbers?: number[] | null;
};
export type PageSearchResult = {
  batchId: string;
  documentId: string;
  sourceName: string;
  pageNumber: number;
  snippet: string;
  rank: number;
};
export type DocumentTypeSuggestion = {
  suggestedType: string;
  confidence: number;
  supportingExamples: number;
  matchingCues: string[];
  reason: string;
};
/**
 * One thing this job is close to learning, and exactly how close.
 *
 * Measured on the real Accounts Payable job: five verified type labels spread
 * one each across five different types, and so no progress toward anything at
 * all. Every miner is gated on repetition WITHIN one type or field, and nothing
 * in the product said so — an operator paying the price of confirmation could
 * not tell a confirmation that unlocks a rule from one that buys nothing.
 */
/**
 * Confidence histograms measured on the job's latest batch, so a review
 * threshold can show what it would cost before it is saved. Buckets are
 * whole percent (index i counts documents whose confidence rounds to i%);
 * "below threshold t" sums the buckets left of t. Null when nothing has
 * been read yet.
 */
export type ReviewImpactProfile = {
  totalDocuments: number;
  documentType: number[];
  boundary: number[];
  anyField: number[];
  fields: FieldImpactHistogram[];
  accuracyEstimate?: ReviewedAccuracyEstimate | null;
};
export type ReviewedAccuracyEstimate = {
  basis: 'original-readings' | 'current-retained-method' | 'reviewed-estimate';
  replayedValues?: number;
  reviewedDocuments: number;
  totalValues: number;
  correctValues: number;
  assumedCorrectValues: number;
  accuracy: number | null;
};
/** ReviewState.WaitReason: one clause of the engine's own review decision. */
export type ReviewWaitReason =
  | 'not-read'
  | 'no-type'
  | 'manual-mode' // PAIGE: read without a setup, in a job in Manual (raised through the core's ReviewState.HostWaitReasons: ManualModeHold)
  | 'start'
  | 'missing-required'
  | 'filing-value-missing'
  | 'type-unconfirmed'
  | 'type-confidence'
  | 'field-confidence'
  | 'field-problem'
  | 'arithmetic'
  | 'table-total'
  | 'line-page'
  | 'problem'
  | 'teaching'
  | 'no-values'
  | 'change-unconfirmed'
  | 'reopened'
  | 'another-look';
/**
 * What a review setting would do to the latest batch, in documents, decided by
 * the engine's own ReviewState (DocumentStore.PreviewReviewRelease).
 */
export type ReviewReleasePreview = {
  mode: ProcessingMode;
  /** Unexported documents nobody has confirmed as a whole. */
  documents: number;
  goWithoutReview: number;
  waitForPerson: number;
  /** Held in Manual, looked at again by Autopilot's AI check, or recorded as exceptions where Autopilot sends documents out unchecked. */
  flagged: number;
  alreadyConfirmed: number;
  /** Flagged documents per reason; one document can count under several. */
  reasons: { reason: ReviewWaitReason; documents: number }[];
  /**
   * Of the same `documents`, what each check alone would hold at its drafted bar (as if
   * switched on), and the confidence each check judges per whole percent (0-100).
   */
  checks?: {
    documentType: number;
    fields: number;
    boundary: number;
    required: number;
    arithmetic: number;
    typeHistogram?: number[] | null;
    fieldsHistogram?: number[] | null;
    boundaryHistogram?: number[] | null;
    /** Of fieldsHistogram, the documents the field check holds: a field's own bar can hold one above the job's bar or let one below it go. */
    fieldsHeldHistogram?: number[] | null;
  } | null;
  /** Not read yet: read again automatically; not review, and cannot be exported until read. */
  cannotExportNotRead?: number;
  /** No type to file it by: cannot be exported until it has one (Manual; Autopilot sends it to exceptions). */
  cannotExportNoType?: number;
  /** Autopilot (ADR-0096): documents that go to the exceptions folder instead; in neither count above. */
  toExceptions?: number;
  /** The batch's documents that already went to the exceptions folder: the headline's "N went to exceptions". */
  sentToExceptions?: number;
  /** The batch's documents a learned junk shape deleted and nobody restored: the headline's "N deleted automatically". */
  deletedAutomatically?: number;
};
export type FieldImpactHistogram = {
  fieldId?: string | null;
  fieldName: string;
  documents: number[];
};

export type OperatorCorrection = {
  eventId: string;
  occurredAt: string;
  kind: string;
  documentId: string;
  fieldName: string;
  before?: string | null;
  after?: string | null;
  /** The road it came by when not the window ("support" for remote support). */
  via?: string | null;
};

export type LearningProgress = {
  readonly kind: 'document-type' | 'field-format' | 'barcode-field';
  readonly subject: string;
  readonly have: number;
  readonly need: number;
  readonly remaining: number;
  /** Documents of this kind already read and waiting for an answer. */
  readonly waiting: number;
  readonly unlocks: string;
};

/** A literal count behind the confirmed-example route for one document type. */
export type LearningAccelerationType = {
  readonly documentType: string;
  readonly confirmed: number;
  /** The current job-specific minimum at which the route may be tested. */
  readonly minimumToTest: number;
  readonly waiting: number;
  /** Current documents actually recognised by this route; this is use, not a prediction or temporal claim. */
  readonly recognisedFromConfirmedExamples: number;
  /** The job's current documents of this type. Absent from engines older than this field. */
  readonly documents?: number;
  /**
   * Of those, the ones nobody looked at: not held for a check, and no person confirmed or
   * changed the type or any field. A document confirmed while teaching counts as reviewed.
   */
  readonly readWithoutReview?: number;
  /**
   * One real page of this type's own documents (setup report only), drawn when no learned
   * layout of the type has a picture yet: a confirmed document's first, otherwise the one
   * Paige was surest of. Absent when no page image is on disk.
   */
  readonly examplePage?: LayoutExamplePage | null;
};

/** One real page of a layout or type, as setup draws it. */
export type LayoutExamplePage = {
  documentId: string;
  previewPath: string;
  width: number;
  height: number;
  confirmed: boolean;
};

/** Production-backed state for scalar field positions learned from confirmed documents. */
export type LearningAccelerationScalarFields = {
  readonly evaluationState: 'checking' | 'settled' | 'failed';
  readonly bestEligibleExamples: number;
  readonly fieldsAtMinimum: number;
  readonly availableFieldRoutes: number;
  readonly observedMappedDocuments: number;
  readonly observedMappedValues: number;
};

/** Facts the Review workspace can use without starting the heavyweight learner. */
export type LearningAccelerationReport = {
  readonly improvementUse?: {
    readonly version: number;
    readonly tableExperiments: number;
    readonly tableDocuments: number;
    readonly tableRows: number;
    readonly fieldExperiments?: number;
    readonly fieldDocuments?: number;
    readonly fieldValues?: number;
    readonly typeImprovements?: number;
    readonly typeDocuments?: number;
    readonly layoutImprovements?: number;
    readonly layoutDocuments?: number;
    readonly boundaryImprovements?: number;
    readonly boundaryPages?: number;
  } | null;
  readonly documentTypes: LearningAccelerationType[];
  readonly layouts: LearnedLayouts;
  readonly scalarFields: LearningAccelerationScalarFields;
  /** PAIGE: each type's own first pages (confirmed first), for setup's cards where no layout has a page yet. */
  readonly typeExamples?: readonly TypeExamplePages[] | null;
  readonly capability?: {
    readonly version: number;
    readonly score: number;
    readonly coverage: number;
    readonly evaluationState: string;
    readonly areas: readonly {
      readonly area: string;
      readonly weight: number;
      readonly tasks: number;
      readonly knownScopes: number;
      readonly validatedScopes: number;
      readonly coverage: number;
    }[];
  } | null;
};

export type LayoutCatchUpReport = {
  readonly evidenceChanged: boolean;
  readonly caughtUp: boolean;
  readonly waitedMilliseconds: number;
};

/** What correcting one batch cost, per hundred pages. Verifications are not corrections. */
export type LearningEffortPoint = {
  readonly batchId: string;
  readonly startedAt: string;
  readonly pages: number;
  readonly corrections: number;
  readonly perHundredPages: number;
};

export type LearningEffort = {
  readonly jobId: string;
  /** False on a single batch. One reading drawn as a trend invites belief in a direction nothing measured. */
  readonly enoughToTrend: boolean;
  readonly batches: readonly LearningEffortPoint[];
};

/**
 * One completed batch as a speed reading, with the share of documents whose
 * type never needed the model — the mechanism by which a job speeds up.
 */
export type JobSpeedPoint = {
  readonly batchId: string;
  readonly completedAt: string;
  readonly pages: number;
  readonly documents: number;
  readonly pagesPerMinute: number;
  readonly recognisedWithoutReader: number;
  readonly recognisedShare: number;
};

export type JobSpeedHistory = {
  readonly jobId: string;
  readonly enoughToTrend: boolean;
  readonly batches: readonly JobSpeedPoint[];
};

/** An on-demand re-read of the job's own recent pages with today's learned state — timed and discarded. */
export type JobSpeedMeasurement = {
  readonly jobId: string;
  readonly pages: number;
  readonly documents: number;
  readonly pagesPerMinute: number;
  readonly recognisedWithoutReader: number;
  readonly recognisedShare: number;
  readonly elapsedSeconds: number;
};

export type LearningFieldScore = {
  readonly fieldName: string;
  readonly verified: number;
  readonly agreed: number;
  readonly agreement: number;
};

export type LearningTypeScore = {
  readonly typeName: string;
  readonly verified: number;
  readonly agreed: number;
  readonly agreement: number;
};

/** What the reader already had right, counted only over answers an operator gave. */
export type LearningScorecard = {
  readonly jobId: string;
  readonly verifiedTypeCount: number;
  readonly verifiedFieldCount: number;
  readonly verifiedBoundaryCount: number;
  readonly typeAgreement: number;
  readonly fieldAgreement: number;
  readonly boundaryAgreement: number;
  /** False until there is enough to say anything. A confident 100% off three labels gets believed. */
  readonly enoughToJudge: boolean;
  /** Each rate answers for its own denominator; the combined total cannot vouch for it. */
  readonly enoughForTypeRate: boolean;
  readonly enoughForFieldRate: boolean;
  readonly enoughForBoundaryRate: boolean;
  readonly types: readonly LearningTypeScore[];
  readonly fields: readonly LearningFieldScore[];
};

/** One background re-judge pass (engine DocumentStore.RejudgeHeldWork): the held documents it released. */
export type HeldWorkRelease = {
  releaseId: string;
  releasedAt: string;
  /** confirmations: from what the operator confirmed; learned-types: a type the job's learner earned the right to settle; review-settings: newly saved Review thresholds; ai-review: the smarter AI's closer look; pulled-back: a released document back in review. */
  cause: 'confirmations' | 'learned-types' | 'review-settings' | string;
  documents: Array<{ batchId: string; documentId: string; waitedFor: string[]; reopened: boolean }>;
  reopened: number;
};

export type JobSuggestion = {
  decisionDetails?: { label: string; value: string }[] | null;
  requiresOperatorDecision?: boolean;
  id: string;
  key: string;
  /* Derived from SUGGESTION rather than spelled again. Written out by hand this
     had already fallen behind: line-item-shift was a kind the engine proposed,
     the constant named, and this union did not admit existed. */
  kind: (typeof SUGGESTION)[keyof typeof SUGGESTION];
  status: 'pending' | 'dismissed' | 'snoozed' | 'never' | 'applied';
  title: string;
  detail: string;
  supportCount: number;
  estimatedPrecision: number;
  /* Where `estimatedPrecision` came from, and it is not decoration: four kinds of
     number share that one field. A held-out rate, a trial's pass/fail expressed as
     1.0, a definitional 1.0, and a constant nobody measured. The drawer ranks on the
     number, so an asserted 0.85 sorts above a measured 0.80 — which is a real thing
     the engine does today, deliberately left alone until somebody decides whether it
     should. Anything that SHOWS the number has to say which kind it is. Mirrors
     SuggestionPrecision in Vocabulary.cs. */
  precisionProvenance: (typeof SUGGESTION_PRECISION)[keyof typeof SUGGESTION_PRECISION];
  createdAt: string;
  updatedAt: string;
  /** The documents this suggestion is about, when it is about specific ones. */
  evidenceDocumentIds?: string[] | null;
  /** The named thing being proposed — the type to add — shown beside the action badge. */
  proposedLabel?: string | null;
  /**
   * A card that adds a type: the fields designed for it in the background from its documents (D10). Absent while
   * not designed yet; empty when the design found none.
   */
  proposedFields?: SuggestedTypeField[] | null;
};
export type JobSuggestionHistoryEntry = {
  id: string;
  suggestionId: string;
  kind: JobSuggestion['kind'];
  title: string;
  /* "self-apply" is a rule that met the bar and applied itself. It is recorded
     apart from "apply" because the operator did not do it and will not
     remember doing it. */
  decision: 'apply' | 'self-apply' | 'undo' | 'not-now' | 'snooze' | 'never';
  currentStatus: JobSuggestion['status'];
  jobSchemaVersion: number;
  occurredAt: string;
};
export type JobSuggestionDecisionRequest = {
  jobId: string;
  suggestionId: string;
  decision: 'not-now' | 'snooze' | 'never';
  snoozedUntil?: string;
};
/** `keptFieldNames`: a new-type card's proposed fields the person kept (D10); none adds the type with no fields. */
export type JobSuggestionApplyRequest = {
  jobId: string;
  suggestionId: string;
  proposedName?: string | null;
  excludedDocumentIds?: string[] | null;
  keptFieldNames?: string[] | null;
};
/** A field the background design proposes for a suggested new document type (DocumentStore.SuggestedTypeFields). */
export type SuggestedTypeField = { name: string; kind: string; description?: string | null };
/** One thing a job has to get right, and how well it is doing at it. */
export type JobAreaAccuracy = {
  area: string;
  seen: number;
  checked: number;
  agreed: number;
  /** Null when too little has been checked to say. Not the same as zero. */
  accuracy: number | null;
  /** Checked over seen. What says whether the number beside it is worth anything. */
  coverage: number;
  note: string;
};

/** How a candidate did against answers it was not shown. */
export type CandidateScore = {
  answered: number;
  right: number;
  wrong: number;
  /** Said nothing. A decline costs one confirmation; a wrong answer costs trust. */
  declined: number;
  precision: number;
  coverage: number;
  /** Right answers earn, wrong ones cost four times as much, declining is free. */
  fitness: number;
};

/** A challenger, its score, and what became of it. */
export type LearningCandidate = {
  adopted?: boolean;
  adoptionHoldReason?: string | null;
  round: number;
  lever: string;
  summary: string;
  /** What this setting changes, in the words of the thing it changes. */
  reasoning: string;
  mining: CandidateScore;
  /** Scored only after it won the search, on answers the search never saw. */
  validation: CandidateScore | null;
  regressedSomething: boolean;
  verdict: string;
};

/** A learned way the reader gets one field wrong, and how reliably. */
export type FieldFault = {
  field: string;
  /** casing, trailing, truncated, transposed. */
  shape: string;
  /** The fault in a sentence an operator would recognise. */
  detail: string;
  with: string | null;
  support: number;
  heldOut: number;
  heldOutRight: number;
  /** Judged on answers it was not mined from. Zero held out means a hypothesis, not a rule. */
  precision: number;
  /** A rule for this is already in force, so it is reported as fixed rather than outstanding. */
  addressed: boolean;
};

export type LearningEvidenceRequirement = {
  id: string;
  label: string;
  current: number;
  required: number;
  remaining: number;
  ready: boolean;
};

export type LearningAreaEligibility = {
  id:
    | 'types'
    | 'fields'
    | 'barcode-reading'
    | 'field-guidance'
    | 'tables'
    | 'table-columns'
    | 'boundaries';
  label: string;
  scope: string | null;
  applicable: boolean;
  requirements: LearningEvidenceRequirement[];
  holdReason?: string | null;
  ready: boolean;
};

export type TrainingReadinessItem = {
  id: string;
  category: 'types' | 'fields' | 'tables';
  name: string;
  ready: boolean;
  needs: string[];
};

export type LearningEligibility = {
  jobId: string;
  evaluatedAt: string;
  readinessItems?: TrainingReadinessItem[] | null;
  areas: LearningAreaEligibility[];
  canStart: boolean;
  teachingRecommendations?: Array<{ documentId: string; opportunityIds: string[] }> | null;
};

export type LearningFieldMeasurement = {
  attempts?: Array<{
    attempt: number;
    right: number;
    total: number;
    beforeRight?: number | null;
    regressions: number;
    adopted: boolean;
    scoreScope: string;
  }> | null;
  sessionId: string;
  documentType: string;
  field: string;
  evidenceKey: string;
  recordedAt: string;
  outcome: string;
  modelReads: number | null;
  judged: number;
  beforeRight: number;
  afterRight: number;
  regressions: number;
  applied: boolean;
};

export type LearningTableMeasurement = {
  attempts?: Array<{
    attempt: number;
    right: number;
    total: number;
    beforeRight?: number | null;
    regressions: number;
    adopted: boolean;
    scoreScope: string;
  }> | null;
  checkStage?: 'training' | 'search' | 'final-repair' | null;
  documentsRead?: number;
  sessionId: string;
  documentType: string;
  layoutId: string;
  evidenceKey: string;
  recordedAt: string;
  outcome: string;
  readerInvocations: number;
  judged: number;
  beforeRight: number;
  afterRight: number;
  regressions: number;
  ready: boolean;
};

export type LearningSessionSummary = {
  id: string;
  jobId: string;
  mode: 'manual' | 'scheduled';
  state: 'running' | 'resting' | 'stopping' | 'stopped' | 'failed' | 'interrupted';
  startedAt: string;
  updatedAt: string;
  endedAt: string | null;
  tried: number;
  refused: number;
  operationalFailures: number;
  lastCheckpoint: LearningStatus | null;
};

/** What learning is doing right now. It runs until it is turned off. */
export type LearningStatus = {
  jobId: string;
  /** What it is working on, so the screen can say so instead of implying it. */
  area: string;
  /** Nothing left to win here. Reported rather than searched around. */
  nothingToImprove: boolean;
  running: boolean;
  stopping?: boolean;
  stopReason?: string | null;
  startedAt: string;
  updatedAt: string;
  tried: number;
  refused: number;
  /** Operational failures seen in this run; monotonic and preserved after Stop. */
  operationalFailureCount: number;
  /** Failed reading-learner comparisons in this run, including ones narrated as a kept reading. */
  learnerFailureCount?: number;
  /** Learner targets stopped after two identical failures; not retried this session. */
  heldLearners?: { learner: string; target: string; reason: string; failures: number }[] | null;
  labelsAvailable: number;
  /**
   * How many of those answers the champion's score was actually measured over —
   * the newest of each type, capped. Copy that says "right on all N" must count
   * this, never labelsAvailable; they differ once a job has more answers than
   * the measuring window holds.
   */
  labelsMeasured: number;
  champion: CandidateScore;
  best: LearningCandidate | null;
  recent: LearningCandidate[];
  lastAppliedTypeComparison?: LearningCandidate[] | null;
  retiredTypeComparisons?:
    { id: string; recordedAt: string; attempts: LearningCandidate[] }[] | null;
  comparisons?:
    | {
        id: string;
        target: string;
        recordedAt: string;
        attempts: NonNullable<LearningFieldMeasurement['attempts']>;
      }[]
    | null;
  /** Where the operator's own answers contradict each other. */
  labelDisagreements: string[];
  /** What the reader keeps getting wrong about this job's fields — the actionable finding. */
  fieldFaults: FieldFault[];
  /** The shapes this job's pages come in, and how much of it they account for. */
  layouts: LearnedLayouts;
  /** What learning actually changed, newest first, in operator sentences. */
  promotions: string[];
  /** What it declined to change and why. */
  refusals: string[];
  narration: string;
};

/** What the job's page shapes turned out to be. */
export type LearnedLayouts = {
  /** Job-wide retained use, not restricted to top layouts. Absent means unavailable on older engines. */
  observedUse?: {
    version: 1;
    typeDocuments: Readonly<Record<string, number>>;
    fullTableRows: number;
  } | null;
  found: number;
  documentsCovered: number;
  documentsSeen: number;
  /**
   * Shapes whose documents were all confirmed as one kind, which may therefore answer
   * the type question without a model call. The rest are still shapes and still worth
   * rules; they simply cannot say what a document is.
   */
  typed: number;
  top: LearnedLayoutSummary[];
  /** Layouts per document type over the whole index; absent from older engines. */
  typeLayouts?: Readonly<Record<string, number>> | null;
  /**
   * The job's type id for each type name the index carries, from the documents an operator
   * confirmed under that name. The index keeps a type's old name until it rebuilds, so a
   * renamed type finds its layouts through this. Ambiguous names are left out; absent from older engines.
   */
  typeIds?: Readonly<Record<string, string>> | null;
  /** Confirmed documents deliberately kept on full reading because their page design is not yet recurring. */
  confirmedHeld?: number;
  /** Concise operator-facing explanation of the settled safety hold. */
  confirmedHoldReason?: string | null;
  /**
   * True while the engine is still building the summary for the current index generation
   * (LearningLoop.LearnedLayouts.SummaryPending): `found`, `typed`, `documentsCovered` and `top`
   * are then placeholders, not measurements, so a screen says it is still counting instead of
   * reporting nothing learned. `documentsSeen` and `observedUse` stay real. Absent from older engines.
   */
  summaryPending?: boolean;
};

export type LearnedLayoutSummary = {
  id: string;
  documentType: string;
  documents: number;
  /** What fraction of the job arrives in this shape. */
  share: number;
  canAnswerType: boolean;
  marks: string[];
  /** Operator-confirmed members of this recurring shape. */
  confirmedDocuments?: number;
  /** Distinct operator-confirmed kinds represented by those members. */
  confirmedKinds?: number;
  /** Whether another confirmed kind existed when naming evidence was measured. */
  hasContrast?: boolean | null;
  /** Scalar fields currently served by a proven mechanical map. */
  fieldMapCount?: number;
  fieldMapBuiltFrom?: number;
  /** Confirmed tables belonging to this layout, whether or not a map passed. */
  tableConfirmedDocuments?: number;
  /** Present only when a proven table map is currently served. */
  tableMapBuiltFrom?: number | null;
  /** Current documents whose stored type provenance names this exact layout route. */
  recognisedFromLayout: number;
  /** Confirmed documents behind the current post-reader numeric-column map. */
  numericTableMapBuiltFrom?: number | null;
  /** Current documents whose rows were supplied by this full table map. */
  fullTableMapUsedDocuments?: number;
  /** Current rows actually supplied by this full table map. */
  fullTableMapUsedRows?: number;
  /** Current documents whose rows were repaired by this numeric-column map. */
  numericTableMapUsedDocuments?: number;
  /** Current rows actually repaired by this numeric-column map. */
  numericTableMapUsedRows?: number;
  /** This layout's name; null or absent while it waits for one. Chosen once as the layout forms, or by a person, and then kept. */
  name?: string | null;
  /** Who gave the name: a person, or Paige once as the layout formed. */
  nameSource?: 'operator' | 'automatic' | null;
  /**
   * The engine's `rename-layout` command takes this, so a name cannot land on a layout that changed
   * meanwhile. Null when the layout cannot be named. The desktop does not rename layouts: they stay
   * internal (2026-09-23).
   */
  nameAnchor?: string | null;
  /** One real page of this layout (setup report only): a confirmed member's when one still has its image. */
  examplePage?: LayoutExamplePage | null;
};

/** PAIGE: a document type's example pages for setup's type cards (the engine's TypeExamplePages). */
export type TypeExamplePages = {
  readonly documentType: string;
  readonly pages: readonly NonNullable<LearnedLayoutSummary['examplePage']>[];
};

export type JobSuggestionApplyResult = {
  job: JobDefinition;
  suggestion: JobSuggestion;
  /**
   * Documents that were waiting to be reviewed and are not any more, because
   * this rule made their reading come out right. The number worth telling the
   * operator: not that a rule was learned, but how much of their queue it took
   * away.
   */
  clearedFromReview: number;
};
export type BatchCorrectionCheckRequest = { jobId: string; batchId: string };
/**
 * One warning sentence the operator has read and is done with. The exact
 * stored string, because the batch header keeps warnings as sentences and the
 * removal must take out precisely the one that was dismissed. Deliberately NOT
 * a correction event: `GetBatchReprocessImpact` counts correction events to
 * decide whether a batch holds protected work, and a dismissal says nothing
 * about any document.
 */
export type BatchWarningDismissRequest = { jobId: string; batchId: string; warning: string };
export type BatchReprocessImpact = {
  batchId: string;
  totalDocumentCount: number;
  untouchedDocumentCount: number;
  protectedDocumentCount: number;
  protectedTypeDocumentCount: number;
  protectedFieldCount: number;
  protectedLineItemDocumentCount: number;
  boundaryChangeCount: number;
  correctionEventCount: number;
  hasProtectedWork: boolean;
  canRefreshFields: boolean;
  canReseparate: boolean;
};

export type DocumentResult = {
  /** A join asked where this document starts: its first page, which reads like a page joined to the document before
   * it (engine ReviewState.QuestionedByJoin). A hold only while that page still opens it and nobody answered. */
  startQuestionedPage?: number | null;
  /** The page whose join asked it. */
  startQuestionedBy?: number | null;
  id: string;
  sourceName: string;
  sourcePath: string;
  sourcePaths?: string[];
  /**
   * A setup sample the person left out of the job (setup-sample-leave-out.ts): it has no type, its file is not
   * imported, it teaches nothing, and choosing a type for it puts it back in the type it was left out of. Kept by the
   * desktop in the setup proposal only; the engine never sees it.
   */
  leftOutOfJob?: { typeId: string; typeName: string } | null;
  documentType: string;
  documentTypeId?: string | null;
  documentTypeSchemaVersion?: number;
  fieldContractSignature?: string;
  documentTypeConfidence: number;
  documentTypeSource?: string | null;
  documentTypeTrust?: number;
  documentTypeOriginalPrediction?: string | null;
  documentTypeOriginalConfidence?: number | null;
  documentTypeExampleSupport?: number;
  documentTypeMatchingCues?: string[];
  pages: PageResult[];
  fields: FieldResult[];
  lineItems: LineItemRow[];
  validationFindings: ValidationFinding[];
  needsReview: boolean;
  /** Teaching-only request; native needsReview retains the operational hold. */
  needsTeaching?: boolean;
  documentTypeVerified?: boolean;
  /** The engine sealed this exact document revision as one whole answer. */
  confirmedAsWhole?: boolean;
  /**
   * Why this document's reading never reached the model, when it did not.
   *
   * Absent means the model answered for it — well or badly. Present means the
   * empty values on it are unasked questions rather than readings, and nothing
   * on this document may be scored, exported, or learned from as an answer.
   */
  notReadReason?: string | null;
  /**
   * Beside `notReadReason`: why the fields were not read. `field-reader-missing` and
   * `field-reader-broken` mean the field reader itself couldn't read (it isn't installed, or fails
   * even on its own test read), so reading again waits until the reader reads. Absent for any
   * other one-off failure, which the engine reads again by itself.
   */
  notReadCause?: 'field-reader-missing' | 'field-reader-broken' | null;
  /** Why it waits for a person: the engine's ReviewState.WaitReason codes, set with needsReview. */
  waitReasons?: ReviewWaitReason[] | null;
  /** PAIGE: already exported, and a later check would hold it for these reasons (paige/flagged-after-export.ts). */
  flaggedAfterExport?: string[] | null;
  /**
   * Autopilot (ai-reviewed) jobs: set once the AI reviewer has finished with a document it could not settle (its
   * reasons, possibly empty). Absent while the reviewer has not yet finished with it.
   */
  aiReviewUnsettled?: string[] | null;
  /**
   * The reader's own name for a document none of the job's types fits ("Purchase Order"), engine-owned; feeds
   * new-type suggestions only. No type is ever guessed from it (ADR-0096).
   */
  readerTypeName?: string | null;
  /**
   * Autopilot's exceptions route (ADR-0096), engine-owned: set when the document goes to the job's exceptions folder
   * instead of its destination, with the reason code and its sentence ("None of this job's document types fits this
   * document."). Honoured only on Autopilot; absent otherwise.
   */
  exceptionRoute?: { reason: string; reasonText: string; detail?: string | null } | null;
  /**
   * For a type the reader answered, the evidence its confidence is measured from: of the job's
   * documents shaped like this one, how many carry a type (peers) and how many carry this one
   * (agree); of the documents the reader gave this type and an operator confirmed, how many it had right.
   */
  typeShapeAgreement?: {
    agree: number;
    peers: number;
    recordRight?: number;
    recordAnswers?: number;
  } | null;
};

export type ProcessResult = {
  /** Once-per-finite-selection OCR preflight; shared group ID prevents double counting across waves. */
  folderPreparation?: {
    groupId: string;
    startedAt: string;
    completedAt: string;
    pageCount: number;
    reusedPageCount: number;
    chunkCount: number;
    imageChunkCount?: number;
    overlapMilliseconds?: number;
    /** Receipt-time envelope, including gaps/pauses; not summed active OCR time. */
    recognitionStartedAt?: string;
    recognitionCompletedAt?: string;
    /** False when resumed legacy checkpoints lack earlier interval endpoints. */
    recognitionIntervalComplete?: boolean;
    timings: NonNullable<ProcessResult['timings']>;
  };
  batchId: string;
  startedAt: string;
  completedAt: string;
  documents: DocumentResult[];
  warnings: string[];
  /**
   * Main-process only, for a scanner wave that held its trailing document: how
   * many of the wave's files the engine committed. Absent on every other result.
   */
  consumedSourceCount?: number;
  /**
   * Sheets set aside as blank before this batch was split.
   *
   * Shown rather than kept quiet: a real duplex run put 120 sheets through the
   * feeder and produced 60 pages, which is correct, and the screen said nothing —
   * so an operator who counted the paper in would conclude half the scan was
   * lost. Zero means nothing was blank; absent means this path does not remove
   * blanks at all.
   */
  blankPagesRemoved?: number | null;
  bootstrapProposal?: {
    documentTypes: JobDocumentTypeDefinition[];
    notes: string[];
    likelyWorkflow?: string | null;
    suggestedTypeChecks?: string[] | null;
    /** How much of the folder this proposal was actually built on. */
    documentsAvailable?: number;
    documentsRead?: number;
    /** Where in reading order each kind first appeared. */
    discoveries?: { typeName: string; afterDocumentsRead: number }[] | null;
    /** Internal representative-sample evidence used when Analyze more extends the proposal. */
    fieldKindEvidence?: Array<{
      documentTypeSemanticId: string;
      fieldSemanticId: string;
      votes: Array<{ kind: string; count: number; firstObservation: number }>;
    }> | null;
  } | null;
  timings?: {
    rasterizationMilliseconds: number;
    recognitionWallMilliseconds: number;
    ocrComputeMilliseconds: number;
    barcodeComputeMilliseconds: number;
    boundaryAnalysisMilliseconds: number;
    extractionMilliseconds: number;
    totalMilliseconds: number;
  };
  workspaceProjection?: 'paged';
  loadedDocumentIds?: string[];
};

export type JobWorkSummary = {
  notYetExported: number;
  needsReview: number;
  exportedAllTime: number;
  destinationLabel: string | null;
};

/**
 * The operator's review of a job, from the engine's correction ledger (owner, 2026-10-04): what
 * the documents list's tips quote. Documents confirmed in the last seven days that waited in
 * Needs review until then, how many of those were confirmed without any change, and how many
 * of the job's latest documents (up to 100) the operator changed.
 */
export type ReviewOutcomes = {
  waitedThisWeek: number;
  waitedConfirmedUnchanged: number;
  recentDocuments: number;
  recentChanged: number;
};

export type BatchWorkspaceOverview = {
  batchId: string;
  startedAt: string;
  completedAt: string;
  documentCount: number;
  pageCount: number;
  readyDocumentCount: number;
  reviewDocumentCount: number;
  warnings: string[];
  timings?: ProcessResult['timings'] | null;
};
export type BatchPageSummary = {
  number: number;
  previewPath: string;
  startsNewDocument: boolean;
  boundaryConfidence: number;
  boundaryReasons?: string[] | null;
};
export type BatchDocumentIndexItem = {
  id: string;
  position: number;
  pageCount: number;
  ready: boolean;
  needsReview: boolean;
  /** Teaching-only request; native needsReview retains the operational hold. */
  needsTeaching?: boolean;
};
export type BatchDocumentSummary = {
  id: string;
  position: number;
  sourceName: string;
  documentType: string;
  documentTypeId: string | null;
  documentTypeSchemaVersion: number;
  fieldContractSignature: string;
  documentTypeConfidence: number;
  documentTypeVerified: boolean;
  needsReview: boolean;
  /** Teaching-only request; native needsReview retains the operational hold. */
  needsTeaching?: boolean;
  ready: boolean;
  lineItemCount: number;
  validationErrorCount: number;
  pages: BatchPageSummary[];
};
export type BatchDocumentWindow = {
  overview: BatchWorkspaceOverview;
  offset: number;
  limit: number;
  documents: BatchDocumentSummary[];
};
export type BatchBoundaryMutationResult = {
  overview: BatchWorkspaceOverview;
  index: BatchDocumentIndexItem[];
  removedDocumentIds: string[];
  documents: DocumentResult[];
  /** A join's lookalikes this save marked to check (engine DocumentStore.JoinLookalikes). */
  lookalikesAdded?: number;
  /** Documents outside the change whose join question was asked or released: read them again. */
  questionsChanged?: string[] | null;
};
export type BatchDocumentWindowRequest = {
  jobId: string;
  batchId: string;
  offset: number;
  limit: number;
};
export type BatchDocumentRequest = { jobId: string; batchId: string; documentId: string };

export type TableReadingPlan = {
  source: 'ocr' | 'image-and-ocr';
  separateItemCode: boolean;
  preserveRowsAndNumbers?: boolean;
};
export type FieldSchema = {
  id?: string;
  semanticId?: string | null;
  name: string;
  kind: string;
  required: boolean;
  readText: boolean;
  readBarcode: boolean;
  description?: string;
  multiple?: boolean;
  barcodeSymbology?: string | null;
  barcodeValuePrefix?: string | null;
  valuePattern?: string | null;
  formatExample?: string | null;
  defaultValue?: string | null;
  readingInstruction?: string | null;
  tableReadingPlan?: TableReadingPlan | null;
  learnedFromCount?: number;
  shiftLineItemColumns?: boolean;
  repairUnbalancedQuantity?: boolean;
  reviewBelow?: number | null;
  reviewBelowSource?: string | null;
};
export type DocumentTypeSchema = {
  id?: string;
  semanticId?: string | null;
  schemaVersion?: number;
  name: string;
  fields: FieldSchema[];
  description?: string;
};
export type BoundaryPagesLikeRequest = { jobId: string; batchId: string; pageNumber: number };
/** Engine `boundary-pages-like`: this batch's pages alike to the joined one (not already joined), and how many pages
 * of that layout in the job's other batches were confirmed as starts (while any are, the layout is not learned). */
export type BoundaryLookalikes = { pages: number[]; separateElsewhere: number };
export type ImportRequest = {
  jobId: string;
  documentTypes: DocumentTypeSchema[];
  separationMode?: string;
  separationBarcodeText?: string | null;
  boundaryOpeningProfiles?: BoundaryOpeningProfile[];
  boundaryContinuationProfiles?: BoundaryContinuationProfile[];
  currentBatchId?: string | null;
};
export type FolderImportRequest = {
  jobId: string;
  token: string;
  documentTypes: DocumentTypeSchema[];
  separationMode?: string;
  separationBarcodeText?: string | null;
  boundaryOpeningProfiles?: BoundaryOpeningProfile[];
  boundaryContinuationProfiles?: BoundaryContinuationProfile[];
};
export type FolderContinuationRequest = Omit<FolderImportRequest, 'token' | 'documentTypes'> & {
  batchId: string;
};
/** Exact operator decision for an unfinished representative operation. */
export type FolderAnalysisOperationDecisionRequest = {
  jobId: string;
  operationId: string;
  evidenceRevision: number;
};
export type FolderAnalysisStatus = {
  includeRemaining?: boolean;
  canIncludeRemaining?: boolean;
  available: boolean;
  /** True while the native process is still building or extending this proposal. */
  running: boolean;
  /** Authoritative native lifecycle; undefined renderer state means it has not been checked yet. */
  state: 'idle' | 'running' | 'recoverable' | 'ready' | 'no-proposal' | 'failed' | 'conflicted';
  /** True only when no unpublished operation can make the visible proposal stale. */
  autoAcceptEligible: boolean;
  /** True when the exact saved operation can be attempted again without choosing the documents anew. */
  resumeAvailable: boolean;
  /** Identity of the current, failed, or last-published analysis operation. */
  operationId: string | null;
  /** Native operation start, retained so elapsed time does not reset after renderer replacement. */
  startedAt: string | null;
  /** Monotonic per-job proposal publication number; zero means no proposal has ever been published. */
  evidenceRevision: number;
  /** Exact bounded native failure retained by the main process across renderer replacement. */
  failure: string | null;
  totalFileCount: number;
  analyzedFileCount: number;
  deeplyAnalyzedDocumentCount: number;
  remainingFileCount: number;
};
export type FolderContinuationResult = FolderAnalysisStatus & { result: ProcessResult };
export type FolderAnalysisEvidence = FolderAnalysisStatus & {
  result: ProcessResult | null;
  /** The proposal this continuation extended, needed to preserve operator edits after reload. */
  previousResult: ProcessResult | null;
  /** Latest native progress snapshot for a replacement renderer. */
  progress: FolderAnalysisProgress | null;
};
export type FolderAnalysisEvidenceSaveRequest = { jobId: string; result: ProcessResult };
export type FolderImportStagingResult = { captures: StagedCapture[]; totalFileCount: number };
/** Bounded current-page display evidence. Not operator truth or extraction input. */
export type PageRecognitionEvidence = {
  previewPath: string;
  pageNumber: number;
  lineCount: number;
  lines: OcrLine[];
  barcodes: BarcodeResult[];
  /** The page's exact word count (the line list is a sample). Absent from engines before 2026-09-27. */
  wordCount?: number;
  /** How far the page was turned to straighten it before reading, positive clockwise; 0 when it was not. */
  deskewDegrees?: number;
  /** Nothing readable on the sheet; it is kept, and left out of documents. */
  blank?: boolean;
};
/**
 * PAIGE: setup's field design, question by question, as the engine reports it
 * (the core's services/document-engine/SetupDesignSnapshot.cs). Real questions asked and answered;
 * only `questionsPlanned` is an estimate.
 */
export type SetupDesignSnapshot = {
  types: SetupTypeDesign[];
  questionsAnswered: number;
  questionsAsking: number;
  questionsPlanned: number;
  answeredMilliseconds: number;
  width: number;
  elapsedMilliseconds: number;
  sinceLastAnswerMilliseconds: number;
  failures?: number;
  lastFailure?: 'unreachable' | 'timed-out' | 'unreadable' | null;
};
export type SetupTypeDesign = {
  name: string;
  state: 'waiting' | 'asking' | 'designed' | 'ready' | 'failed';
  answered: number;
  asking: number;
  planned: number;
  question?: string | null;
  fields: string[];
  tableColumns: number;
};
export type FolderAnalysisProgress = {
  jobId: string;
  stage:
    | 'preparing'
    | 'reading'
    | 'recognizing'
    | 'organizing'
    | 'understanding'
    | 'finalizing'
    | 'complete';
  completed: number;
  total: number;
  sourceName?: string | null;
  pageCount: number;
  barcodeCount: number;
  documentCount: number;
  documentTypes: string[];
  fieldCount: number;
  lineItemCount: number;
  elapsedMilliseconds: number;
  stageElapsedMilliseconds: number;
  /** Source pages whose document has completed the current stage. */
  completedPageCount: number;
  activity?: {
    id: string;
    kind:
      | 'source'
      | 'straighten'
      | 'page'
      | 'ocr'
      | 'barcode'
      | 'boundary'
      | 'document'
      | 'proposal'
      | 'resume'
      | 'learning';
    status: 'queued' | 'started' | 'completed' | 'failed';
    label: string;
    detail?: string | null;
    pageNumber: number;
    previewPath?: string | null;
    tags?: string[] | null;
    evidence?: PageRecognitionEvidence | null;
    discoveredDocumentTypes?: string[] | null;
    /** A setup step's own count, e.g. 3 of 5 document types designed; total 0 means none. */
    stepCompleted?: number;
    stepTotal?: number;
    /** The page within its own file; pageNumber is the page's place in the batch. */
    sourcePageNumber?: number;
    /** PAIGE: every type's field design as it stands, on each "bootstrap:fields" event (setup-design-progress.ts). */
    design?: SetupDesignSnapshot | null;
    /** PAIGE: while setup waits for Cortexa to start ("setup-waiting-for-reader"; paige/cortexa-wait.ts). */
    readerWait?: { endpoint: string; waitedMilliseconds: number; limitMilliseconds: number } | null;
    /**
     * While a local reasoner chooses a type's fields: its answers for this type so far, how many
     * a designed type has taken in this setup (0 until one is done), and milliseconds per answer.
     */
    questionsAnswered?: number | null;
    questionsExpected?: number;
    answerMilliseconds?: number;
  } | null;
  completedDocument?: DocumentResult | null;
  /**
   * The engine repeating its last state because nothing new happened for a
   * moment: the work is alive, not stuck. Carries no new activity; never renews
   * the request's silence deadline.
   */
  heartbeat?: boolean;
  /** The long-running step the engine is inside right now, e.g. "Grouping pages into documents". */
  phase?: string | null;
  /** How long the engine has been inside `phase`. */
  phaseElapsedMilliseconds?: number;
  /** On a heartbeat: how long since the engine last reported real work. */
  quietMilliseconds?: number;
};
export type CaptureProcessingProgress = Omit<FolderAnalysisProgress, 'jobId'> & {
  jobId: string;
  captureId: string;
  completedDocument?: DocumentResult | null;
  /**
   * A folder wave's: the files of each wave of the same folder whose every page
   * has been read, by capture id, only ever rising. Pages of later waves are read
   * ahead under the first wave's id, so the count names the wave it belongs to.
   */
  filesRead?: Record<string, number>;
};
/**
 * The retained copy of a capture's progress: the counters, without the payload.
 *
 * `activity` and `completedDocument` are what a live event carries for a screen
 * watching it frame by frame. What is kept for a screen that comes BACK is the
 * stage and the numbers, so neither is retained and neither is sent a second
 * time when a returning workspace asks what this job is doing.
 */
export type LiveCaptureProgress = Omit<CaptureProcessingProgress, 'activity' | 'completedDocument'>;
// Reprocessing names the stored batch, never a renderer-supplied file list.
// Large workspaces deliberately omit source paths from projection skeletons;
// the engine owns the complete provenance and enumerates it authoritatively.
export type RetryRequest = {
  jobId: string;
  batchId: string;
  documentTypes: DocumentTypeSchema[];
  separationMode?: string;
  separationBarcodeText?: string | null;
  boundaryOpeningProfiles?: BoundaryOpeningProfile[];
  boundaryContinuationProfiles?: BoundaryContinuationProfile[];
};
export type ReviewedReprocessRequest = RetryRequest;
/**
 * The job is part of the request because the document is read again under its
 * lookups: a field populated from a data table has to be filled from that table
 * on the way back out of review, exactly as it was on the way in.
 */
export type PreparedDocumentReextractRequest = {
  jobId: string;
  document: DocumentResult;
  documentType: DocumentTypeSchema;
};
/** A box the operator drew around a table, in the page image's pixels. */
export type TableRegion = {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
};
/**
 * "Find the table" on a document whose reading has no rows: the thorough table reader, then the page's
 * printed columns, or only the operator's box. The engine refuses a document that has any row.
 */
export type FindDocumentTableRequest = {
  jobId: string;
  batchId: string;
  documentId: string;
  documentType: DocumentTypeSchema;
  region?: TableRegion;
};
/** The stored document afterwards; `found` false leaves it unchanged. */
export type FindDocumentTableResult = {
  found: boolean;
  rows: number;
  route: 'thorough' | 'printed' | 'marked';
  document: DocumentResult;
};
export type PreparedDocumentReclassifyRequest = {
  jobId: string;
  document: DocumentResult;
  documentTypes: DocumentTypeSchema[];
};
export type BoundaryDocumentReclassifyRequest = {
  jobId: string;
  batchId: string;
  documentIds: string[];
  documentTypes: DocumentTypeSchema[];
};
export type BatchSchemaRefreshRequest = {
  jobId: string;
  batchId: string;
  documentTypes: DocumentTypeSchema[];
  formatChecksOnly?: boolean;
};
export type PageSearchRequest = { jobId: string; batchId?: string; query: string; limit?: number };
export type StagedCapture = {
  id: string;
  jobId: string;
  sourceKind: 'files' | 'scanner' | 'simulator';
  sourceLabel: string;
  fileCount: number;
  /**
   * The durable scheduling regime chosen when a folder was partitioned.
   *
   * Processor-paced waves run one at a time so an operator's confirmations can
   * rebuild the job's fast routes before the next wave takes its snapshot. It is
   * absent on ordinary file picks, scanner captures, and legacy manifests.
   */
  waveMode?: 'accelerated' | 'processor-paced';
  /**
   * The folder this capture is one wave of, with every wave it was split into
   * (discarded waves left out). Waves are scheduling only: the operator chose
   * ONE folder, so every count they see is drawn from this whole, never from a
   * single wave. Absent on file picks, scans and legacy manifests.
   */
  folderImport?: FolderImportIdentity;
  /**
   * `held`: kept pages of an interrupted scan, waiting for the operator to
   * check them. A held capture is not read, and nothing queued after it is read
   * ahead of it, so the part of a document it may hold cannot reach review or
   * automatic export until the operator has decided about the rest of the paper.
   */
  status: 'pending' | 'failed' | 'held';
  createdAt: string;
  error?: string | null;
  /** Why the scan that produced these pages ended early, when it did. */
  interruption?: ScanInterruption | null;
  /** Held captures only: the last kept page image, for the operator to check against the feeder. */
  lastKeptPage?: string | null;
  /**
   * How many times recognition has actually been STARTED on this capture,
   * counted where it starts rather than where it is asked for, so a manual
   * retry and an automatic one are the same event.
   *
   * Without it, "failed once" and "failed the same way nine times" are the same
   * record: retryStagedCapture only flipped the status back to pending and
   * cleared the error, so every attempt erased the evidence of the last one.
   * The renderer re-queues outage-shaped failures on every mount, so a capture
   * that always fails that way was retried silently and for ever, and neither
   * the operator nor the log could tell.
   */
  attempts?: number;
  /**
   * What an interrupted run of THIS capture already finished and will not do
   * again — read back from the engine's wave journal before anything restarts.
   *
   * Absent when the capture has never been started, which is a different thing
   * from `{ pages: 0, documents: 0 }`: the first means nothing was lost because
   * nothing had begun, the second means a crash whose work did not survive. The
   * operator is owed both sentences, and a single "waiting" line could say
   * neither.
   */
  recovered?: StagedCaptureRecovery;
  /**
   * Set by a host whose files are WAITING, not interrupted: Paige holds a
   * failed-status capture until the account has pages (credit) again. The
   * renderer then says "Waiting for pages" instead of "Import unfinished".
   * The desktop never sets it.
   */
  waitingForPages?: boolean;
};
/**
 * Finished work sitting in the engine's journal for one acquisition, counted
 * before anything restarts.
 *
 * `interruptedAt` is when the dead run last finished something, which is the
 * fact that turns "something is waiting" into "this stopped at 14:12".
 *
 * Named for the capture rather than for the wave on purpose: the engine's own
 * `WaveRecovery` is a different record — what a resumed run actually reused,
 * counted as it ran — and the two must not be mistaken for each other.
 */
export type StagedCaptureRecovery = {
  pages: number;
  documents: number;
  interruptedAt: string | null;
};
/** One chosen folder, however many waves it was staged as. */
export type FolderImportIdentity = { id: string; waves: { id: string; fileCount: number }[] };
export type StageDocumentsRequest = { jobId: string };
/**
 * `operationId` is chosen by the renderer so progress and Stop can be scoped
 * to this one scan before its result exists; a late event or a late Stop from
 * an earlier scan cannot touch it.
 */
export type StageScanRequest = { jobId: string; source: ScannerSource; operationId?: string };
export type ScanInterruption = {
  reason:
    'stopped' | 'scanner-error' | 'timed-out' | 'host-exit' | 'app-closed' | 'incomplete-page';
  /** The scanner's own words, when it gave any. */
  message: string | null;
  /** Complete page images kept. Authoritative: counted from disk after the scanner stopped writing. */
  keptPages: number;
  /** Page files that were not complete images; never counted or read. */
  excludedPages: number;
  interruptedAt: string;
};
/**
 * How a physical scan ended.
 *
 * - completed: the whole run is queued.
 * - stopped / failed: ended early; `keptPages` may be zero.
 * - not-queued: pages are kept on disk but could not be added to the queue;
 *   they are retried by `recoverRetainedScans` and on the next start.
 */
export type ScanOutcome = {
  kind: 'completed' | 'stopped' | 'failed' | 'not-queued';
  captureId: string;
  keptPages: number;
  excludedPages: number;
  reason: ScanInterruption['reason'] | null;
  message: string | null;
  /** False while a stopped or unresponsive scanner program may still hold the device. */
  scannerReleased: boolean;
};
export type StageCaptureResult = { canceled: boolean; capture?: StagedCapture; scan?: ScanOutcome };
export type RetainedScanRecoveryResult = { published: number; failed: string | null };
export type ProcessStagedCaptureRequest = ImportRequest & { captureId: string };

/**
 * A pretend scanner model, for demonstrating capture on a computer with no
 * device attached. Speed is the point of the choice: the three models are an
 * order of magnitude apart and the feed really runs at the rate shown.
 */
export type SimulatedScannerModel = {
  id: string;
  name: string;
  tier: string;
  pagesPerMinute: number;
  description: string;
};
/**
 * Where this workstation keeps scans, batches and everything it learns.
 *
 * `fixedByEnvironment` is true when CAPTUREPOINT_DATA_ROOT is set, which a test
 * harness does; the screen shows the path and refuses to offer a change rather
 * than appearing to accept one that would be ignored.
 */
export type StorageLocation = {
  /** The folder Settings shows: the one chosen for the next start while a restart is pending. */
  path: string;
  /**
   * The folder this run keeps its work in, whatever is chosen for the next start: what the
   * export screen's default backup folder is built from, since exports land there until the
   * restart.
   */
  inUsePath: string;
  isDefault: boolean;
  fixedByEnvironment: boolean;
  /** The disk of `path`: the pending folder's disk while a restart is pending. */
  freeBytes: number | null;
  totalBytes: number | null;
  /**
   * The disk of `inUsePath`, where exports and backups land until the restart: the same as
   * freeBytes/totalBytes unless a restart is pending. Absent from an older main process.
   */
  inUseFreeBytes?: number | null;
  inUseTotalBytes?: number | null;
  /** Set once a change is chosen: it applies when the application restarts. */
  restartRequired?: boolean;
};

/** What the simulated feeder is loaded with, drawn from the bundled samples. */
export type SimulatedScannerTray = {
  corpus: string;
  label: string;
  pageCount: number;
  documentCount: number;
};
/**
 * Every stack that could be loaded, so the operator can choose one.
 *
 * `pageCount` is absent here on purpose: counting sheets means opening every
 * multi-page TIFF in every installed corpus, and this list is built when the
 * dialog mounts. The document count comes free from the directory listing, and
 * the exact sheet count arrives with `tray` once a stack is chosen.
 *
 * Chosen rather than assigned because the alternative wasted a real testing
 * session: an operator who came to look at accounts payable was handed freight
 * logistics, which the dialog named correctly and gave them no way to change.
 */
export type SimulatedScannerStack = {
  corpus: string;
  label: string;
  documentCount: number;
  /** True when the set ships a pre-trained job, so trying it needs no setup. */
  ready?: boolean;
};
export type SimulatedScannerCatalog = {
  models: SimulatedScannerModel[];
  tray: SimulatedScannerTray | null;
  stacks: SimulatedScannerStack[];
};
/** `documents` feeds only those files of the stack (the tour's five), in the stack's own order. */
export type StartSimulatedScanRequest = {
  jobId: string;
  modelId: string;
  corpus?: string;
  documents?: string[];
};
export type StopSimulatedScanRequest = { jobId: string };
/**
 * One sheet, off a real scanner, while the scan is still running.
 *
 * The simulated scanner streamed every sheet to the screen and a real one
 * streamed nothing: `stageScan` awaited the whole acquisition and answered once,
 * at the end, so an operator feeding paper into their own scanner watched a
 * disabled button and a spinner while the demonstration device showed each page
 * arriving full size. That is backwards for a product whose subject is scanning,
 * and it is what the operator reported after their first real batch.
 *
 * There is no total: a feeder does not know how much paper is in it. `sheet` is
 * what has actually come across the wire, which is the number the operator can
 * check against the stack in their hand.
 */
export type ScanProgress = {
  jobId: string;
  captureId: string;
  operationId?: string;
  sheet: number;
  elapsedMilliseconds: number;
  previewPath?: string | null;
};
/**
 * Simulated scans only: `previewPath` arrives in a second event for the same
 * page. Rasterizing a sheet costs more than the gap between sheets on a fast
 * model, so it happens off the feed's critical path: the page is reported the
 * moment it lands, and its image follows when it is ready. The feed's timing is
 * the thing being demonstrated and must not be paid for by the picture of it.
 */
export type SimulatedScanProgress = {
  jobId: string;
  captureId: string;
  page: number;
  pageCount: number;
  elapsedMilliseconds: number;
  pagesPerMinute: number;
  previewPath?: string | null;
};
export type SimulatedScanResult = StageCaptureResult & {
  achievedPagesPerMinute?: number;
  stopped?: boolean;
};

/**
 * One label-and-value pair stored against a break sheet's identifier, and the
 * operator's decision about whether it appears on the paper.
 *
 * `printed` is required. It rode along undeclared for a while — the flag was
 * added to the objects the renderer builds but not to the contract, and only
 * survived because no fresh object literal was ever constructed and excess
 * property checking therefore never fired. Anyone normalising a request to its
 * declared shape would have dropped it and printed every withheld value with no
 * type error to show for it. The main process fails closed on a missing flag;
 * this makes it impossible to omit in the first place.
 */
export type BreakSheetField = { label: string; value: string; printed: boolean };
/** The paper a run is drawn on; the symbols keep their physical size on both. */
export type BreakSheetPaper = 'letter' | 'a4';
export type BreakSheetRun = {
  runId: string;
  jobId: string | null;
  kind: 'plain' | 'fields';
  sheetCount: number;
  sheetIds: string[];
  createdAt: string;
  paper?: BreakSheetPaper;
};
/** `jobId` and `paper` are Paige's: a run belongs to a job there, and is printed on Letter or A4. */
export type PlainBreakSheetRequest = { count: number; jobId?: string; paper?: BreakSheetPaper };
export type FieldBreakSheetRequest = {
  jobId: string;
  sheets: { documentType: string | null; fields: BreakSheetField[] }[];
  paper?: BreakSheetPaper;
};
/**
 * Whose history is being asked for. "Printed before" is a job's own list: a prepared
 * sheet carries values for one job's fields and indexes nothing anywhere else, so a
 * run from another job listed here is a reprint that separates documents and then
 * silently fills nothing in.
 */
export type ListBreakSheetRunsRequest = { jobId: string };
export type OpenBreakSheetRunRequest = { runId: string };
export type CorrectionRequest = {
  jobId: string;
  batch: ProcessResult;
  correction: {
    kind: string;
    targetId: string;
    beforeJson?: string;
    afterJson?: string;
    metadataJson?: string;
  };
};
/** Ctrl+Z after a value was saved: the field goes back as it was and the edit leaves the job's ground truth (engine: undo-field-edit). */
export type FieldEditUndoRequest = {
  jobId: string;
  batchId: string;
  documentId: string;
  fieldName: string;
  expectedField: FieldResult;
};
export type DocumentCorrectionRequest = {
  jobId: string;
  batchId: string;
  document: DocumentResult;
  correction: CorrectionRequest['correction'];
  expectedDocument?: DocumentResult;
  expectedJobSchemaVersion?: number;
};
export type DocumentCorrectionBatchEntry = {
  document: DocumentResult;
  corrections: CorrectionRequest['correction'][];
};
export type DocumentCorrectionsRequest = {
  jobId: string;
  batchId: string;
  correctionEntries: DocumentCorrectionBatchEntry[];
};
export type DocumentCorrectionsResult = {
  applied: number;
  missingDocumentIds: string[];
};
export type BoundaryMutationRequest = {
  jobId: string;
  batchId: string;
  position: number;
  removedDocumentIds: string[];
  documents: DocumentResult[];
  /**
   * The stored documents the replacements were built from, in the same order as
   * removedDocumentIds. The engine refuses the change (boundary-revision-changed)
   * if any of them changed since, so a rewrite can never replace newer work.
   */
  expectedDocuments?: DocumentResult[];
  correction: CorrectionRequest['correction'];
};
export type PageRemovalRequest = {
  jobId: string;
  batchId: string;
  documentId: string;
  pageNumber: number;
  correction: CorrectionRequest['correction'];
};
export type PageRestoreRequest = {
  jobId: string;
  batchId: string;
  documentId: string;
  page: PageResult;
  correction: CorrectionRequest['correction'];
};
/**
 * Delete or Restore (ADR-0096): documents of one batch. Delete is for jobs on Manual and documents not yet exported;
 * a deleted document leaves Needs review, the document list and export for the job's Deleted view, and the store keeps
 * its record and pages. Restore puts it back where it was. Values a person confirmed stay confirmed.
 */
export type DocumentDeletionRequest = {
  jobId: string;
  batchId: string;
  documentIds: string[];
};
/** Why one document was not deleted or restored. */
export type DocumentDeletionRefusal = {
  documentId: string;
  reason: 'not-found' | 'exported' | 'deleted' | 'not-deleted';
};
/** What a Delete or Restore did: the documents it moved, and the ones it refused. */
export type DocumentDeletionResult = {
  done: string[];
  refused: DocumentDeletionRefusal[];
};
/** One page of a deleted document, for the Deleted view's thumbnails. */
export type DeletedDocumentPage = {
  number: number;
  previewPath: string;
  sourcePageNumber: number;
};
/**
 * One document in a job's Deleted view. `deletedBy` is "person" or "learned-junk" (an adopted junk page shape deleted
 * it as it arrived; `reasonText` is then its Activity row, "Deleted automatically: looks like earlier pages that
 * weren't documents."). `teaches` is what the delete taught: "junk", "not-for-this-job" (with `kindName`, the type or the
 * reader's own name) or "nothing".
 */
export type DeletedDocument = {
  jobId: string;
  batchId: string;
  documentId: string;
  sourceName: string;
  documentType: string;
  pageCount: number;
  pages: DeletedDocumentPage[];
  deletedAtUtc: string;
  deletedBy: 'person' | 'learned-junk';
  teaches: 'junk' | 'not-for-this-job' | 'nothing';
  kindName?: string | null;
  shapeId?: string | null;
  reasonText?: string | null;
  automatic: boolean;
};
/** One page of a job's Deleted view, newest delete first. */
export type DeletedDocumentList = {
  documents: DeletedDocument[];
  offset: number;
  limit: number;
  total: number;
};
export type DeletedDocumentsRequest = {
  jobId: string;
  offset?: number;
  limit?: number;
  /** Only the documents deleted this way (Activity lists the automatic ones); every delete when absent. */
  deletedBy?: 'person' | 'learned-junk';
};
/**
 * Turn page: one page of a stored document a quarter turn clockwise (or two, or three). The engine turns a copy of
 * the page image, reads the page again, reads the document's values again from it, and records the correction; the
 * answer is the document as stored afterwards. `documentType` is the document's own type when it has one (the way
 * Find the table sends it); without one the page is still turned and read.
 */
export type PageTurnRequest = {
  jobId: string;
  batchId: string;
  documentId: string;
  pageNumber: number;
  quarterTurns: 1 | 2 | 3;
  documentType?: DocumentTypeSchema;
};
/** Where a running Turn page is: `step` of `steps`, and the step's words ("Reading the page again"). */
export type PageTurnProgress = {
  jobId: string;
  documentId: string;
  pageNumber: number;
  step: number;
  steps: number;
  phase: string | null;
};
export type DocumentTypeSuggestionRequest = { jobId: string; batchId: string; documentId: string };
export type ConfirmedTypeProjectionRequest = {
  jobId: string;
  batchId: string;
  documentTypes: DocumentTypeSchema[];
  loadedDocumentIds?: string[];
};
export type FieldInterpretationRequest = { kind: string; value: string };
export type ReviewPanelKind = 'viewer' | 'fields' | 'line-items';
export type ReviewPanelState = { panel: ReviewPanelKind; detached: boolean };

import type { BenchmarkRun, MachineDescription, StoredBenchmarkRun } from './benchmark-telemetry';
import type {
  SpeedCheckAvailability,
  SpeedCheckDepth,
  SpeedCheckDetail,
  SpeedCheckScheduleState,
  SpeedCheckSchedulePreferences,
  SpeedCheckState,
} from './benchmark-plan';
import type { ClusterMemberId, ClusterOutcome, ClusterView } from './cortexa-cluster';

export type {
  ClusterActivity,
  ClusterContribution,
  ClusterJoinRequest,
  ClusterMember,
  ClusterMemberId,
  ClusterMemberState,
  ClusterOutcome,
  ClusterRefusal,
  ClusterView,
  ClusterWorkKind,
  NearbyCluster,
} from './cortexa-cluster';
// Types only, and nothing at run time. `helperIdentityCode` used to be
// re-exported from here — the one module the renderer imports its whole
// vocabulary from — which put the derivation of the confirmation code inside the
// renderer's bundle. It lives in `main/helper-identity.ts` now, on the side of
// the process boundary that has the fingerprints.

export type {
  BenchmarkRun,
  BenchmarkDataset,
  BenchmarkDatasetResult,
  BenchmarkStageId,
  BenchmarkPhase,
  StageMeasurement,
  StageResult,
  MachineDescription,
  StoredBenchmarkRun,
  AccelerationProfile,
  HardwareProfile,
} from './benchmark-telemetry';

export type {
  SpeedCheckAvailability,
  SpeedCheckBlocker,
  SpeedCheckDepth,
  SpeedCheckDetail,
  SpeedCheckDatasetDetail,
  SpeedCheckProgress,
  SpeedCheckScheduleState,
  SpeedCheckSchedulePreferences,
  SpeedCheckState,
  SpeedCheckTrigger,
  SeparationAccuracy,
  SpeedTestSamplesRoute,
} from './benchmark-plan';

import type {
  CloudAccountSummary,
  CloudDestinationInput,
  CloudDestinationSummary,
  CloudPlace,
  CloudProviderKind,
  CloudProvidersAndAccounts,
  CloudRepositoryField,
  CloudSignInStatus,
  CloudTestResult,
} from './cloud-export';

export type CapturePointDesktopApi = {
  /**
   * Main places this value in the preload's launch arguments before the window
   * exists. The renderer can therefore theme its document synchronously, before
   * React or an asynchronous IPC round trip has a chance to paint.
   */
  readonly initialAppearance: AppearanceMode;
  getAccentColors(): Promise<import('./accent-colors').AccentColors>;
  setAccentColor(
    mode: AppearanceMode,
    color: string,
  ): Promise<import('./accent-colors').AccentColors>;
  onAccentColorsChanged(
    callback: (colors: import('./accent-colors').AccentColors) => void,
  ): () => void;
  getAppearance(): Promise<AppearanceMode>;
  /** Support access (ADR-0088): the one switch, and when it turns itself off. */
  getSupportAccess(): Promise<SupportAccessState>;
  /** On starts seven days; off ends it at once. Answers with the state that now holds. */
  setSupportAccess(enabled: boolean): Promise<SupportAccessState>;
  /** A job changed outside this window (a support request): re-read what that job's own path would have. */
  onJobsChanged(callback: (event: JobsChangedEvent) => void): () => void;
  /** Support requests this window runs through its own handlers; each is answered with replySupportWindowCommand. */
  onSupportWindowCommand(callback: (event: SupportWindowCommandEvent) => void): () => void;
  replySupportWindowCommand(reply: SupportWindowCommandReply): Promise<void>;
  /** The folder this job watches, or null (ADR-0090). Job setup › Watched folder (WatchedFolderSection.tsx). */
  getWatchedFolder(jobId: string): Promise<WatchedFolderSetting | null>;
  /** The native folder picker; the chosen folder, or null when cancelled. Choosing does not save it: setWatchedFolder does. */
  chooseWatchedFolder(jobId: string): Promise<string | null>;
  /** Pause (false) or resume (true) the job's folder, keeping it chosen. Resuming is refused with a sentence as choosing is. */
  setWatchedFolderEnabled(jobId: string, enabled: boolean): Promise<WatchedFolderSetting | null>;
  /** What arrives in the job's folder (ADR-0091): scan stacks, or one document per file. */
  setWatchedFolderSeparation(
    jobId: string,
    separation: 'whole' | 'stack',
  ): Promise<WatchedFolderSetting | null>;
  watchedFolderDetails(jobId: string): Promise<WatchedFolderDetails>;
  /** Open the job's watched folder, or its Unprocessable folder, in Explorer. */
  openWatchedFolder(jobId: string, which: 'folder' | 'set-aside'): Promise<void>;
  /** Choose the job's watched folder, or stop watching with null. Refused with a sentence when the folder cannot be one. */
  setWatchedFolder(jobId: string, path: string | null): Promise<WatchedFolderSetting | null>;
  /** What Paige itself (not the person asking) can do in a folder. Changes nothing. */
  testWatchedFolder(path: string): Promise<WatchedFolderTestResult>;
  watchedFolderStatus(): Promise<WatchedFolderStatus[]>;
  onWatchedFolderStatusChanged(callback: (status: WatchedFolderStatus[]) => void): () => void;
  /** Per job: what is waiting, held or failing with nobody at the computer, and what needs a person. */
  unattendedStatus(): Promise<UnattendedStatus>;
  getTooltips(): Promise<boolean>;
  setTooltips(enabled: boolean): Promise<boolean>;
  onTooltipsChanged(callback: (enabled: boolean) => void): () => void;
  setAppearance(mode: AppearanceMode): Promise<AppearanceMode>;
  onAppearanceChanged(callback: (mode: AppearanceMode) => void): () => void;
  getInterfaceZoom(): Promise<InterfaceZoomState>;
  changeInterfaceZoom(action: InterfaceZoomAction): Promise<InterfaceZoomState>;
  onInterfaceZoomChanged(callback: (state: InterfaceZoomEvent) => void): () => void;
  /**
   * Follow capturepoint:// links (docs/product/app-links.md). The callback gets the
   * link waiting now, if any, and each one that arrives later, once each. A link
   * already taken when the returned function is called is still delivered.
   */
  onAppLink(callback: (route: AppLinkRoute) => void): () => void;
  getRuntimeInfo(): Promise<DesktopRuntimeInfo>;
  getEngineHealth(): Promise<EngineHealth>;
  /**
   * Whether every accelerated stage is on the hardware it should be, with the
   * evidence for each answer.
   *
   * Slower than {@link getEngineHealth} and asked far less often: it waits on
   * the model runtime, shells out to the display driver, and reads stage
   * timings back out of the store. The result is cached in the main process, so
   * `refresh: true` is what the panel's re-run button sends.
   */
  getWorkstationHealth(refresh?: boolean): Promise<WorkstationHealthReport>;
  /** A real computer check: measures the graphics card now, retries the speed boost, then reports. */
  retrySpeedBoost?(): Promise<SpeedBoostRecheck>;
  getExtractionPreference(): Promise<ExtractionPreference>;
  setExtractionPreference(preference: ExtractionPreference): Promise<ExtractionPreference>;
  /** Settings > Performance: this computer's graphics cards and which of them Paige may use. */
  getGraphicsCards?(): Promise<GraphicsCardsView>;
  setGraphicsCards?(allowed: GraphicsCardsAllowed): Promise<GraphicsCardsView>;
  /** Stop a scan on the wire, or only the named operation. Resolves true when there was one to stop. */
  cancelScan(operationId?: string): Promise<boolean>;
  getStorageLocation(): Promise<StorageLocation>;
  chooseStorageLocation(): Promise<StorageLocation>;
  onWorkstationHealthChanged(callback: (report: WorkstationHealthReport) => void): () => void;
  listJobs(): Promise<JobDefinition[]>;
  getJobCardPreview(jobId: string): Promise<string | null>;
  getJobProcessingState(jobId: string): Promise<JobProcessingState>;
  setJobProcessingPaused(jobId: string, paused: boolean): Promise<JobProcessingState>;
  onJobProcessingStateChanged(callback: (state: JobProcessingState) => void): () => void;
  saveJob(job: JobDefinition): Promise<JobDefinition>;
  resetLearning(jobId: string, request: LearningResetRequest): Promise<LearningResetResult>;
  /** Calibration on or off (Learning's switch, shared/calibration.ts): the job as stored, its schema version unchanged. */
  setCalibration(jobId: string, on: boolean): Promise<JobDefinition>;
  /** Counts only: what removing this type would do, and whether it can run now. */
  previewDocumentTypeRemoval(jobId: string, typeId: string): Promise<DocumentTypeRemovalImpact>;
  /**
   * Removes a saved type and only its learning. Its unexported documents lose the type, and
   * the automatic re-read reads each again among the remaining types, never forcing one: a type
   * that fits is used; with none (or no type left), Manual holds it for the operator and
   * Autopilot sends it to the exceptions folder (ADR-0096).
   */
  removeDocumentType(
    jobId: string,
    request: DocumentTypeRemovalRequest,
  ): Promise<DocumentTypeRemovalResult>;
  deleteJob(request: JobDeletionRequest): Promise<void>;
  /**
   * Writes this job's setup to a file the operator chooses. Null when they cancel.
   *
   * Settings only. The file is built from an allow-list rather than filtered out of
   * the job record, so nothing the learner wrote can reach it, and the file says both
   * what it carries and what it left behind.
   */
  exportJobTemplate(request: {
    jobId: string;
    jobName: string;
  }): Promise<JobTemplateExportResult | null>;
  /** Opens a template and stands it up as a NEW job. Null when the picker is cancelled. */
  importJobTemplate(): Promise<JobTemplateImportResult | null>;
  onJobSetupFileWorking(callback: (work: JobSetupFileWork) => void): () => void;
  /**
   * The pre-trained job setup shipped with one sample set, imported through the same
   * job-template import. Null when that set has no setup file: the caller then
   * creates an ordinary new job.
   *
   * `purpose` "tour": the job is the tour's, so telemetry never counts its work as
   * the person's own.
   */
  importSampleJobSetup(corpus: string, purpose?: 'tour'): Promise<JobTemplateImportResult | null>;
  /** Open the sample job now with the standard reader; its reader weights keep downloading and take over when they land. */
  skipSampleReaderDownload(corpus: string): Promise<void>;
  onSampleReaderDownload(callback: (progress: SampleReaderDownloadProgress) => void): () => void;
  listLegacyJobs(connectionSource?: string): Promise<LegacyInstallation>;
  listLegacyImports(): Promise<LegacyImportStatus[]>;
  importLegacyJob(request: LegacyImportRequest): Promise<LegacyImportStatus>;
  legacyImportStatus(jobId: string): Promise<LegacyImportStatus>;
  analyzeLegacyImport(jobId: string): Promise<LegacyImportStatus>;
  pauseLegacyImport(jobId: string): Promise<LegacyImportStatus>;
  chooseExportDestination(jobId: string): Promise<ExportDestinationSelection | null>;
  /**
   * The one-click first export folder (owner decision 2, 2026-09-25; ADR-0096, decision 35):
   * Documents\Paige\<job name>\exports, created empty and issued like a
   * chosen folder. It only fills in the Export route's destination; nothing exports.
   */
  offerDefaultExportDestination(
    jobId: string,
    jobName: string,
  ): Promise<ExportDestinationSelection>;
  /** Whether an earlier Paige saved its connection on this computer; nothing is opened. */
  detectLegacyInstall?(): Promise<boolean>;
  /**
   * Change folder for the documents Paige can't file (ADR-0096): any local folder, issued like a chosen
   * export folder; saving it is `exceptionsToken` on Export's save.
   */
  chooseExceptionsFolder?(jobId: string): Promise<ExportDestinationSelection | null>;
  /** Open folder on Export's exceptions stop: the job's saved exceptions folder, as an Explorer window. */
  openExceptionsFolder?(jobId: string): Promise<void>;
  /**
   * The folders a job's export settings would get, read only: nothing is made or saved. For a job with no saved
   * folder yet, Export shows these instead of a guess.
   */
  getExportFolderDefaults?(jobId: string): Promise<ExportFolderDefaults>;
  getExportSettings(jobId: string): Promise<ExportJobSettings | null>;
  getExportCapabilities(): Promise<ExportCapabilities>;
  getStarterFilingPlan(jobId: string): Promise<FilingPlan>;
  suggestFilingPlan(jobId: string): Promise<FilingAdvisorResult>;
  /** Examples from the job's current documents; with a document type, up to `limit` (1-50) of that type only. */
  previewFilingPlan(
    jobId: string,
    filingPlan: FilingPlan,
    focus?: { documentTypeId?: string; limit?: number },
  ): Promise<FilingPlanPreview>;
  saveExportSettings(request: ExportSettingsSaveRequest): Promise<ExportJobSettings>;
  previewExport(jobId: string): Promise<ExportReadinessPreview>;
  runExport(jobId: string, documentIds?: string[]): Promise<ExportRunResult>;
  listExportedDocuments(request: ExportDocumentListRequest): Promise<ExportDocumentPage>;
  /** "root" opens the job's export folder that holds this document, not the document's own subfolder. */
  openExportedDocument(exportId: string, target: 'pdf' | 'folder' | 'root'): Promise<void>;
  returnExportToWork(exportId: string): Promise<boolean>;
  cancelContentCentralExport(exportId: string): Promise<boolean>;
  retryExport(exportId: string): Promise<ExportDocumentSummary>;
  /** @deprecated Use retryExport; retained for an older renderer/preload pair. */
  retryContentCentralExport(exportId: string): Promise<ExportDocumentSummary>;
  listDestinationConnections(): Promise<DestinationConnectionSummary[]>;
  /** Export to SharePoint/OneDrive, Google Drive and Dropbox (docs/planning/desktop-cloud-exports.md). */
  cloudExportProviders(): Promise<CloudProvidersAndAccounts>;
  /** Opens the provider's sign-in page in the default browser; poll the status until it ends. */
  cloudExportSignIn(kind: CloudProviderKind): Promise<CloudSignInStatus>;
  cloudExportSignInStatus(signInId: string): Promise<CloudSignInStatus>;
  cloudExportCancelSignIn(signInId: string): Promise<CloudSignInStatus>;
  cloudExportSignOut(accountId: string): Promise<{ destinations: number }>;
  cloudExportBrowse(accountId: string, parentId?: string, query?: string): Promise<CloudPlace[]>;
  cloudExportNewFolder(accountId: string, parentId: string, name: string): Promise<CloudPlace>;
  cloudExportDestination(jobId: string): Promise<CloudDestinationSummary>;
  cloudExportSaveDestination(
    jobId: string,
    input: CloudDestinationInput,
  ): Promise<CloudDestinationSummary>;
  cloudExportRemoveDestination(jobId: string): Promise<{ removed: boolean }>;
  cloudExportTest(jobId: string): Promise<CloudTestResult>;
  cloudExportSendEarlier(jobId: string): Promise<{ queued: number }>;
  cloudExportRetry(jobId: string): Promise<{ queued: number }>;
  /** Sends the documents an earlier destination left unsent to the current one. */
  cloudExportSendUnsent(jobId: string): Promise<{ queued: number }>;
  /**
   * Nucleus One: opens Paige's protected key window; the key goes from it to the engine sealed,
   * never through the page. With an account, the new key replaces that account's. Null when cancelled.
   */
  cloudExportConnectKey(
    kind: CloudProviderKind,
    accountId?: string,
  ): Promise<CloudAccountSummary | null>;
  /** A repository's fields at a place (Nucleus One's project fields), for the field map. */
  cloudExportFields(accountId: string, locationId: string): Promise<CloudRepositoryField[]>;
  /** After a person looked: sends once more the documents Paige could not tell had arrived. */
  cloudExportResendUnconfirmed(jobId: string): Promise<{ queued: number }>;
  connectContentCentral(
    request: ContentCentralConnectionRequest,
  ): Promise<DestinationConnectionSummary | null>;
  recheckContentCentral(connectionId: string): Promise<DestinationConnectionSummary>;
  replaceContentCentralKey(connectionId: string): Promise<DestinationConnectionSummary | null>;
  removeContentCentral(connectionId: string): Promise<number>;
  listContentCentralCatalogs(
    connectionId: string,
    query?: string,
    cursor?: string,
  ): Promise<ContentCentralCatalogPage>;
  getContentCentralCatalog(
    connectionId: string,
    catalogId: string,
  ): Promise<ContentCentralCatalogDefinition>;
  proposeContentCentralBinding(
    jobId: string,
    connectionId: string,
    catalogId: string,
    choices: ContentCentralMappingChoice[] | undefined,
    proposalIntentId: string,
  ): Promise<ContentCentralBindingProposal>;
  createContentCentralJob(
    connectionId: string,
    catalogId: string,
    jobName: string,
    acknowledgedBoundJobId?: string,
  ): Promise<CatalogBackedJobResult>;
  findContentCentralJob(
    connectionId: string,
    catalogId: string,
  ): Promise<BoundCatalogJobSummary | null>;
  proposeContentCentralCatalog(
    jobId: string,
    connectionId: string,
    filingPlan: FilingPlan,
    proposalIntentId: string,
  ): Promise<ContentCentralProvisioningPlan>;
  cancelContentCentralProposalIntent(
    jobId: string,
    connectionId: string,
    proposalIntentId: string,
  ): Promise<boolean>;
  getPendingContentCentralProposal(jobId: string): Promise<PendingContentCentralProposal | null>;
  discardPendingContentCentralProposal(
    request: PendingContentCentralProposalDecision,
  ): Promise<boolean>;
  applyContentCentralCatalog(
    jobId: string,
    connectionId: string,
    planId: string,
    planFingerprint: string,
  ): Promise<ExportConfigurationResult>;
  verifyContentCentralCatalogApply(
    jobId: string,
    connectionId: string,
    planId: string,
    planFingerprint: string,
  ): Promise<ContentCentralApplyCommitProof | null>;
  getJobDestinationBinding(jobId: string): Promise<JobDestinationBinding | null>;
  previewContentCentralFiling(jobId: string): Promise<ContentCentralFilingPreviewResult>;
  getLatestBatch(jobId: string): Promise<ProcessResult | null>;
  getLatestBatchOverview(jobId: string): Promise<BatchWorkspaceOverview | null>;
  getJobWorkSummary(jobId: string): Promise<JobWorkSummary>;
  getReviewOutcomes(jobId: string): Promise<ReviewOutcomes>;
  /**
   * What this job's pages are doing right now, from the main process rather
   * than from whichever view happened to be mounted when the engine said it.
   *
   * Null means nothing is in flight — which, together with a null overview, is
   * the only honest reading of "this job is empty". A job whose first
   * acquisition has not committed a batch yet answers here instead, so the
   * workspace can wait it out on real numbers rather than offering the intake
   * screen to a workstation that is already reading.
   */
  getLiveCaptureProgress(jobId: string): Promise<LiveCaptureProgress | null>;
  getBatchDocumentIndex(
    request: Pick<BatchDocumentRequest, 'jobId' | 'batchId'>,
  ): Promise<BatchDocumentIndexItem[]>;
  getBatchDocumentWindow(request: BatchDocumentWindowRequest): Promise<BatchDocumentWindow>;
  getBatchDocument(request: BatchDocumentRequest): Promise<DocumentResult | null>;
  retryDocuments(request: RetryRequest): Promise<ProcessResult>;
  reprocessReviewed(request: ReviewedReprocessRequest): Promise<ProcessResult>;
  reextractPreparedDocument(request: PreparedDocumentReextractRequest): Promise<DocumentResult>;
  findDocumentTable(request: FindDocumentTableRequest): Promise<FindDocumentTableResult>;
  reclassifyPreparedDocument(request: PreparedDocumentReclassifyRequest): Promise<DocumentResult>;
  reclassifyBoundaryDocuments(request: BoundaryDocumentReclassifyRequest): Promise<ProcessResult>;
  refreshBatchSchema(request: BatchSchemaRefreshRequest): Promise<ProcessResult>;
  searchPages(request: PageSearchRequest): Promise<PageSearchResult[]>;
  getScannerSources(): Promise<ScannerDiscovery>;
  chooseRepresentativeFiles(): Promise<FolderSelection>;
  /** With `jobId`, a folder brought into that existing job (its own intake folder is remembered; the picker says Add pages). */
  chooseDocumentFolder(jobId?: string): Promise<FolderSelection>;
  importFolderDocuments(request: FolderImportRequest): Promise<ProcessResult>;
  continueFolderAnalysis(request: FolderContinuationRequest): Promise<FolderContinuationResult>;
  /** Resume the exact durable representative operation after app or engine replacement. */
  resumeFolderAnalysis(
    jobId: string,
    decision?: FolderAnalysisOperationDecisionRequest,
  ): Promise<FolderContinuationResult>;
  /** Explicitly discard one exact terminal/recoverable operation without changing published evidence. */
  abandonFolderAnalysisOperation(
    request: FolderAnalysisOperationDecisionRequest,
  ): Promise<FolderAnalysisStatus>;
  stopFolderAnalysisOperation(
    request: FolderAnalysisOperationDecisionRequest,
  ): Promise<FolderAnalysisStatus>;
  getFolderAnalysisStatus(jobId: string): Promise<FolderAnalysisStatus>;
  includeRemainingFolderDocuments(jobId: string): Promise<FolderAnalysisStatus>;
  getFolderAnalysisEvidence(jobId: string): Promise<FolderAnalysisEvidence>;
  saveFolderAnalysisEvidence(request: FolderAnalysisEvidenceSaveRequest): Promise<void>;
  stageFolderImport(jobId: string): Promise<FolderImportStagingResult>;
  stageFolderWaves(request: { jobId: string; token: string }): Promise<FolderImportStagingResult>;
  onFolderAnalysisProgress(callback: (progress: FolderAnalysisProgress) => void): () => void;
  stageDocuments(request: StageDocumentsRequest): Promise<StageCaptureResult>;
  stageScan(request: StageScanRequest): Promise<StageCaptureResult>;
  /** `corpus` chooses which stack to describe; omitted means the preference order. */
  getSimulatedScanners(corpus?: string): Promise<SimulatedScannerCatalog>;
  startSimulatedScan(request: StartSimulatedScanRequest): Promise<SimulatedScanResult>;
  stopSimulatedScan(request: StopSimulatedScanRequest): Promise<void>;
  onScanProgress(callback: (progress: ScanProgress) => void): () => void;
  onSimulatedScanProgress(callback: (progress: SimulatedScanProgress) => void): () => void;
  createPlainBreakSheets(request: PlainBreakSheetRequest): Promise<BreakSheetRun>;
  createFieldBreakSheets(request: FieldBreakSheetRequest): Promise<BreakSheetRun>;
  listBreakSheetRuns(request: ListBreakSheetRunsRequest): Promise<BreakSheetRun[]>;
  openBreakSheetRun(request: OpenBreakSheetRunRequest): Promise<void>;
  listStagedCaptures(jobId: string): Promise<StagedCapture[]>;
  processStagedCapture(request: ProcessStagedCaptureRequest): Promise<ProcessResult>;
  onCaptureProcessingProgress(callback: (progress: CaptureProcessingProgress) => void): () => void;
  discardStagedCapture(captureId: string): Promise<void>;
  /**
   * Puts a capture back as pending, and answers with `attempts` as the manifest
   * held it a moment ago — before the start this retry is about to spend.
   *
   * Deliberately one behind rather than a guess at the next number: the count is
   * the main process's, taken where recognition actually begins, and a view that
   * added one to it would be reporting an attempt that may never happen. It
   * understates and is corrected at the next listing; it never overstates, which
   * matters because the screen turns it into "Tried N times".
   */
  retryStagedCapture(captureId: string, explicitOperatorRetry?: boolean): Promise<number>;
  /** The operator has checked an interrupted scan's kept pages: let them be read. */
  releaseHeldCapture(captureId: string): Promise<void>;
  /** Queue kept scanner pages whose earlier hand-off to the queue failed. */
  recoverRetainedScans(jobId: string): Promise<RetainedScanRecoveryResult>;
  readPagePreview(previewPath: string, purpose?: 'thumbnail'): Promise<string>;
  /** Background component download, for the status bar. */
  getRuntimeComponentStatus(): Promise<RuntimeComponentDownloadStatus>;
  /** Retry a failed background component download; resumes the partial file. */
  retryRuntimeComponents(): Promise<RuntimeComponentDownloadStatus>;
  onRuntimeComponentStatusChanged(
    callback: (status: RuntimeComponentDownloadStatus) => void,
  ): () => void;
  /** Current auto-update state, for the update banner. */
  getUpdateStatus(): Promise<UpdateStatus>;
  /** Apply a downloaded update now and relaunch, instead of waiting for the next quit. */
  installUpdateNow(): Promise<UpdateInstallResult>;
  /**
   * Look for an update now, rather than waiting for the next scheduled check.
   *
   * Resolves once the check has finished, so the caller can report the outcome
   * from the status that follows rather than leaving the operator guessing.
   */
  checkForUpdateNow(): Promise<UpdateStatus>;
  onUpdateStatusChanged(callback: (status: UpdateStatus) => void): () => void;
  /** The stored channel and the two options, with the sentence under each. */
  getUpdateChannel(): Promise<UpdateChannelSetting>;
  /**
   * Change the channel and reckon with whatever was already underway.
   *
   * Resolves as soon as the old work has been dealt with and the new check has
   * STARTED, not when it finishes — a check is a network round trip, and the
   * operator needs the answer about their downloaded package immediately. The
   * outcome of the new check arrives through `onUpdateStatusChanged`.
   */
  setUpdateChannel(channel: UpdateChannel): Promise<UpdateChannelChangeResult>;
  copyText(text: string): Promise<void>;
  showCopyMenu(text: string): Promise<void>;
  saveCorrection(request: CorrectionRequest): Promise<ProcessResult>;
  saveDocumentCorrection(request: DocumentCorrectionRequest): Promise<DocumentResult>;
  saveDocumentCorrections(request: DocumentCorrectionsRequest): Promise<DocumentCorrectionsResult>;
  /** Rejects when the edit can no longer be taken back exactly (the document changed, was confirmed or exported, or learning has used it). */
  undoFieldEdit(request: FieldEditUndoRequest): Promise<DocumentResult>;
  waitForLayoutCatchUp(jobId: string): Promise<LayoutCatchUpReport>;
  saveBoundaryMutation(request: BoundaryMutationRequest): Promise<BatchBoundaryMutationResult>;
  /** The batch's pages nobody has ruled on whose top reads like this page's, by the measure the
   * continuation learner clusters joins on. Review offers them after a join. */
  boundaryPagesLike(request: BoundaryPagesLikeRequest): Promise<BoundaryLookalikes>;
  removePage(request: PageRemovalRequest): Promise<BatchBoundaryMutationResult>;
  restorePage(request: PageRestoreRequest): Promise<BatchBoundaryMutationResult>;
  /** Delete (ADR-0096): on Manual only; rejects on Autopilot. Documents already exported are refused, never deleted. */
  deleteDocuments(request: DocumentDeletionRequest): Promise<DocumentDeletionResult>;
  /** Restore: deleted documents back where they were. */
  restoreDocuments(request: DocumentDeletionRequest): Promise<DocumentDeletionResult>;
  /** The job's Deleted view, newest delete first. */
  listDeletedDocuments(request: DeletedDocumentsRequest): Promise<DeletedDocumentList>;
  turnPage(request: PageTurnRequest): Promise<BatchBoundaryMutationResult>;
  onTurnPageProgress(callback: (progress: PageTurnProgress) => void): () => void;
  suggestDocumentType(
    request: DocumentTypeSuggestionRequest,
  ): Promise<DocumentTypeSuggestion | null>;
  applyConfirmedTypeExamples(request: ConfirmedTypeProjectionRequest): Promise<ProcessResult>;
  listJobSuggestions(jobId: string): Promise<JobSuggestion[]>;
  getLearningProgress(jobId: string): Promise<LearningProgress[]>;
  getLearningAcceleration(jobId: string): Promise<LearningAccelerationReport>;
  getLearningCapability(
    jobId: string,
  ): Promise<NonNullable<LearningAccelerationReport['capability']>>;
  /** Newest first. `limit` (1 to 500) asks for more than the engine's default 100. */
  getOperatorCorrections(jobId: string, limit?: number): Promise<OperatorCorrection[]>;
  /** Every name each of the job's types has carried (its saved versions); absent from older shells. */
  getDocumentTypeNames?(jobId: string): Promise<DocumentTypeName[]>;
  getReviewImpactProfile(jobId: string): Promise<ReviewImpactProfile | null>;
  /** Re-judges the latest batch under a drafted review setting; saves nothing. */
  previewReviewRelease(jobId: string, draft: JobDefinition): Promise<ReviewReleasePreview | null>;
  autoTuneReviewBars(jobId: string): Promise<JobDefinition | null>;
  getLearningEffort(jobId: string): Promise<LearningEffort>;
  getJobSpeedHistory(jobId: string): Promise<JobSpeedHistory>;
  measureJobNow(job: JobDefinition): Promise<JobSpeedMeasurement>;
  onMeasureJobProgress(callback: (progress: FolderAnalysisProgress) => void): () => void;
  getLearningScorecard(jobId: string): Promise<LearningScorecard>;
  listJobSuggestionHistory(jobId: string): Promise<JobSuggestionHistoryEntry[]>;
  /** Rules that met the bar and applied themselves, so the shell can say one did. */
  listSelfAppliedSuggestions(jobId: string): Promise<JobSuggestion[]>;
  /** Applies everything the job has learned to the work still waiting. Returns how many documents stopped needing review. */
  applyLearningToWaitingWork(jobId: string): Promise<number>;
  /** What the background re-judge released from review as truth arrived, newest first; since = a releasedAt already seen. */
  listHeldWorkReleases(jobId: string, since?: string): Promise<HeldWorkRelease[]>;
  /** Put a re-judge release's documents back in review. Returns how many went back. */
  reopenHeldWorkRelease(jobId: string, releaseId: string): Promise<number>;
  /** Tells the engine which document the operator has open, so nothing in the background changes its verdict. */
  noteOpenDocument(jobId: string, documentId: string | null): Promise<boolean>;
  /** Each area of a job, its accuracy, and how much of it has ever been checked. */
  jobAccuracy(jobId: string): Promise<JobAreaAccuracy[]>;
  /** What the reader keeps getting wrong about a job's fields. Costs one query; needs no session. */
  learnFieldFaults(jobId: string): Promise<FieldFault[]>;
  /**
   * Turns learning on for a job. It runs until stopped (or `stopsAt`). It adopts only what passes its
   * gates, and reports what it promoted and what it refused.
   */
  startLearning(
    jobId: string,
    mode?: 'manual' | 'scheduled',
    stopsAt?: number,
  ): Promise<LearningStatus>;
  learningScheduleState(): Promise<LearningScheduleState>;
  initializeLearningSchedule(legacy: LearningSchedule): Promise<LearningScheduleState>;
  setLearningSchedule(schedule: LearningSchedule): Promise<LearningScheduleState>;
  learningWorkstation(): Promise<{
    idleSeconds: number;
    onBattery: boolean;
    freeMemoryBytes: number;
    processing: boolean;
    scheduledHold: string | null;
  }>;
  learningStatus(jobId: string): Promise<LearningStatus | null>;
  learningEligibility(jobId: string, batchId?: string): Promise<LearningEligibility>;
  teachingRecommendations(
    jobId: string,
    batchId: string,
  ): Promise<Array<{ documentId: string; opportunityIds: string[] }>>;
  learningHistory(jobId: string): Promise<LearningSessionSummary[]>;
  /** The layout reader's per-type learning for one job (ADR-0086, engine "layout-reader-models"). */
  layoutReaderModels?(jobId: string): Promise<LayoutReaderLearningState>;
  learningFieldResults(jobId: string): Promise<LearningFieldMeasurement[]>;
  learningTableResults(jobId: string): Promise<LearningTableMeasurement[]>;
  stopLearning(jobId: string): Promise<LearningStatus | null>;
  decideJobSuggestion(request: JobSuggestionDecisionRequest): Promise<JobSuggestion>;
  applyJobSuggestion(request: JobSuggestionApplyRequest): Promise<JobSuggestionApplyResult>;
  undoJobSuggestion(request: JobSuggestionApplyRequest): Promise<JobSuggestionApplyResult>;
  batchHasCorrections(request: BatchCorrectionCheckRequest): Promise<boolean>;
  getBatchReprocessImpact(request: BatchCorrectionCheckRequest): Promise<BatchReprocessImpact>;
  /** Removes one warning from the stored batch, for good. Answers with the warnings that remain. */
  dismissBatchWarning(request: BatchWarningDismissRequest): Promise<string[]>;
  interpretField(request: FieldInterpretationRequest): Promise<FieldInterpretation>;
  /** Opens a file picker, imports the chosen delimited file, and profiles its columns. */
  importDataTable(request: {
    jobId: string;
    name: string;
    replacesDataTableId?: string;
  }): Promise<DataTableImportResult | null>;
  /** The job's imported tables and the lookup rules built on them. */
  listDataTables(jobId: string): Promise<DataTableCatalog>;
  /**
   * Reads a page of an imported table's rows so an operator can see what was read.
   * With `query`, only rows holding that text in any value (ignoring capitals) are paged.
   */
  previewDataTable(request: {
    jobId: string;
    dataTableId: string;
    limit?: number;
    offset?: number;
    query?: string;
  }): Promise<DataTablePreview>;
  saveLookupRule(request: {
    jobId: string;
    rule: LookupRuleDefinition;
  }): Promise<LookupRuleDefinition>;
  deleteLookupRule(request: { jobId: string; ruleId: string }): Promise<boolean>;
  deleteDataTable(request: { jobId: string; dataTableId: string }): Promise<boolean>;
  /** Renames a list. Lookups point at it by identity, so none of them change. */
  renameDataTable(request: { jobId: string; dataTableId: string; name: string }): Promise<boolean>;
  /** Runs one value through a rule, saved or not, and reports the rung that answered. */
  testLookup(request: {
    jobId: string;
    value: string;
    ruleId?: string;
    rule?: LookupRuleDefinition;
  }): Promise<LookupMatch>;
  /** Measures a rule against its own table, before any document is scanned. */
  validateLookupRule(request: {
    jobId: string;
    ruleId?: string;
    rule?: LookupRuleDefinition;
    sampleSize?: number;
  }): Promise<LookupValidationReport>;
  /**
   * Asks whether a value out of this field could ever be a value in that column,
   * before anything is tested.
   *
   * No rule is needed and no match is attempted: the answer is in the two columns.
   * A `different-shape` verdict is what stops the wire between the document and the
   * list, so the operator who typed a vendor number at a name-matching rule is told
   * on sight instead of after a failure.
   */
  fieldCanMatchColumn(request: LookupFitRequest): Promise<LookupFitVerdict>;
  /**
   * What the rule as it stands fills and what it costs, over this field's own
   * recent readings — the "fills 31 of 40 · no wrong rows" beside each rung.
   *
   * Distinct from `validateLookupRule`, which measures a rule against its own table
   * with no documents involved. The report says which of the two it is in
   * `measuredFrom`, because printing one under the other's sentence is a lie.
   */
  measureRecentReadings(request: {
    jobId: string;
    sourceFieldName: string;
    ruleId?: string;
    rule?: LookupRuleDefinition;
    readingCount?: number;
  }): Promise<LookupCostReport>;
  openReviewPanel(panel: ReviewPanelKind, jobId?: string): Promise<void>;
  closeReviewPanel(panel: ReviewPanelKind): Promise<void>;
  onClosePermissionRequest(listener: (request: ClosePermissionRequest) => void): () => void;
  replyClosePermission(reply: ClosePermissionReply): Promise<void>;
  getDetachedReviewPanels(): Promise<ReviewPanelKind[]>;
  onReviewPanelStateChanged(callback: (state: ReviewPanelState) => void): () => void;
  /** Verified v6 trial or paid authority. Credentials and device identity never cross preload. */
  getV6LicenseContact(): Promise<CapturePointV6ContactInput | null>;
  getV6LicenseState(): Promise<CapturePointV6LicenseView>;
  /** The Lite plan's pages (included, packs, what is left here). Not metered: `metered` false. */
  getPageAllowance?(): Promise<import('./v6-page-allowance').PageAllowanceView>;
  /** Check in with Ademero now: send counted pages, bring the balance up to date. */
  checkInPages?(): Promise<import('./v6-page-allowance').PageAllowanceView>;
  /** Open a card checkout for 1 to 100 packs of 1,000 pages; the packs arrive at the next check-in. */
  buyPages?(packs: number): Promise<{ started: true } | { started: false; message: string }>;
  onPageAllowanceChanged?(
    callback: (view: import('./v6-page-allowance').PageAllowanceView) => void,
  ): () => void;
  refreshV6License(): Promise<CapturePointV6LicenseView>;
  extendV6Trial(contact: CapturePointV6ContactInput): Promise<CapturePointV6LicenseView>;
  requestV6Activation(contact: CapturePointV6ContactInput): Promise<CapturePointV6LicenseView>;
  /** Ask Ademero staff for more trial time after the free extension (a note travels as `message`). */
  requestV6MoreTime(contact: CapturePointV6ContactInput): Promise<CapturePointV6LicenseView>;
  onV6LicenseStateChanged(callback: (state: CapturePointV6LicenseView) => void): () => void;
  /** The opt-in, the first-week progress record and opt-in product events (ADR-0080). */
  getEngagement?(): Promise<import('./engagement').EngagementView>;
  setEngagementConsent?(
    input: import('./engagement').EngagementConsentInput,
  ): Promise<import('./engagement').EngagementView>;
  onEngagementChanged?(callback: (view: import('./engagement').EngagementView) => void): () => void;
  /** Records an onboarding first on this computer (main's clock; kept locally, never sent). */
  noteOnboardingMilestone?(
    milestone: import('./engagement').OnboardingMilestone,
  ): Promise<import('./engagement').EngagementView>;
  /** One-time move of milestone times the renderer once kept in browser storage. */
  adoptOnboardingMilestones?(
    record: Record<string, unknown>,
  ): Promise<import('./engagement').EngagementView>;
  /** Opens Stripe Checkout in the browser for one station; the license binds after payment. */
  startCheckout?(
    input: import('./engagement').CheckoutStartInput,
  ): Promise<import('./engagement').CheckoutStartResult>;
  /** Checks a partner code with Phone Home and returns the partner's name, so a typo can't lose the partner's credit. */
  lookupPartnerCode?(code: string): Promise<import('./engagement').PartnerCodeLookupResult>;
  /** "Bill us instead": sends the buyer, plan and partner code to Ademero as an invoice request. */
  requestInvoice?(
    input: import('./engagement').InvoiceRequestInput,
  ): Promise<import('./engagement').InvoiceRequestResult>;
  /** The invoice request waiting with Ademero, or null (settled once this computer is licensed). */
  getPendingInvoiceRequest?(): Promise<import('./engagement').PendingInvoiceRequest | null>;
  /** The four email asks (shared/email-asks.ts): what each is set to, and the email to fill the row with. */
  getEmailAsks?(): Promise<import('./email-asks').EmailAsksView>;
  /** One ask's Send: done, waiting to be sent (offline), or refused with friendly words and the exact error. */
  sendEmailAsk?(
    input: import('./email-asks').EmailAskInput,
  ): Promise<import('./email-asks').EmailAskResult>;
  /** Turn off that one email (a learning note: that one job). */
  turnOffEmailAsk?(
    input: import('./email-asks').EmailAskTurnOffInput,
  ): Promise<import('./email-asks').EmailAsksView>;
  onEmailAsksChanged?(callback: (view: import('./email-asks').EmailAsksView) => void): () => void;
  /** Product telemetry (ADR-0081): a screen, feature, dialog, error, notice, timing or funnel step. Fire and forget. */
  trackTelemetry?(input: import('./telemetry').RendererTelemetryInput): void;
  /** What is waiting to be sent, and the last send, for Settings › License. */
  getTelemetry?(): Promise<import('./telemetry').TelemetryView>;
  describeBenchmarkMachine(): Promise<MachineDescription>;
  recordBenchmarkRun(run: BenchmarkRun): Promise<StoredBenchmarkRun>;
  getBenchmarkHistory(): Promise<StoredBenchmarkRun[]>;
  /**
   * Start a speed check. Resolves when the check finishes, or rejects if it stops.
   * `from`: which button started it (telemetry's `speed-test.start`; main checks it).
   */
  startSpeedCheck(
    depth: SpeedCheckDepth,
    pauseImports?: boolean,
    from?: 'hint' | 'settings' | 'card',
  ): Promise<void>;
  cancelSpeedCheck(): Promise<void>;
  getSpeedCheckState(): Promise<SpeedCheckState>;
  /** Whether a check could start right now, and what is in the way if not. */
  getSpeedCheckAvailability(): Promise<SpeedCheckAvailability>;
  /** The local-only companion records: separation accuracy and plain-language notes. */
  getSpeedCheckDetails(): Promise<SpeedCheckDetail[]>;
  getPerformanceCalibration(): Promise<PerformanceCalibration | null>;
  /**
   * Graphics memory as it stood when this session's latest finished speed test started (one reading,
   * taken after the product engine stopped and before the test's own engine started), with that
   * test's run id, or null before one. A test that stopped or failed keeps none: pair it with a score
   * only when the run ids match. Optional so a renderer outside the desktop shell reads it as "no reading".
   */
  getGraphicsMemoryAtTestStart?(): Promise<GraphicsMemoryAtScoredTest | null>;
  getSpeedCheckSchedule(): Promise<SpeedCheckScheduleState>;
  setSpeedCheckSchedule(
    preferences: SpeedCheckSchedulePreferences,
  ): Promise<SpeedCheckScheduleState>;
  onSpeedCheckStateChanged(callback: (state: SpeedCheckState) => void): () => void;
  /**
   * The speed test card's Get sample documents (availability.samples): puts the sample documents back,
   * downloading them first when they are not on this computer. Rejects with a plain line first.
   */
  getSpeedTestSamples?(): Promise<void>;
  /** Bytes of the sample documents download as they arrive. */
  onSpeedTestSamplesProgress?(
    callback: (progress: { receivedBytes: number; totalBytes: number }) => void,
  ): () => void;

  /**
   * The cluster this computer is in, who else is in it, and what each is doing.
   *
   * Never carries a secret — the renderer receives status, not credentials — and
   * never a certificate, because the identity code a human reads is derived from
   * one and a screen holding both could answer its own question.
   */
  getCluster(): Promise<ClusterView>;
  /**
   * Start or stop listening for other computers.
   *
   * Call it with `true` while the cluster screen is on show and `false` when it
   * is not: a computer in no cluster should not be listening to the network when
   * nobody is looking. Listening continues regardless while this computer is in a
   * cluster or asking to join one, because its peers' comings and goings decide
   * what work is sent where.
   */
  setClusterWatching(enabled: boolean): Promise<ClusterView>;
  /** Create a cluster and name it. This computer is immediately a cluster of one. */
  createCluster(name: string): Promise<ClusterOutcome>;
  /**
   * Ask to join a cluster seen on the network, named by its opaque handle.
   *
   * Asking is all it does. The request rides this computer's own announcement,
   * a member shows it along with this computer's identity code, and a human at
   * that desk decides. Nothing reaches out and nothing is granted here.
   */
  joinCluster(clusterId: ClusterMemberId): Promise<ClusterOutcome>;
  /** Stop asking. Closes the window in which an admission would be accepted. */
  cancelClusterJoin(): Promise<ClusterView>;
  /**
   * Admit a computer that is asking to join. Any member may do this.
   *
   * **Admitting is transitive**: the computer will be able to read pages from
   * every computer in the cluster, not only this one. The screen says so.
   *
   * Before pressing it, the operator checks the identity code on this screen
   * against the one that computer is showing. That is one glance, and it is the
   * only thing between admitting the machine they meant and admitting whatever
   * answered — a name on a network is free.
   *
   * A refusal **resolves** with `{ ok: false, reason, message }` rather than
   * rejecting: `reason` is for the code and `message` is for the operator, shown
   * word for word.
   */
  admitIntoCluster(requestId: ClusterMemberId): Promise<ClusterOutcome>;
  /**
   * Stop showing a computer's request to join.
   *
   * Nothing is sent to it. A machine that asked is not told it was refused,
   * because answering would confirm to an unadmitted computer that this cluster
   * is here — and it can go on asking, which costs nothing once no screen is
   * showing it. This is the operator saying "not that one", not a decision
   * about the machine, so it is forgotten when the application restarts.
   */
  ignoreClusterRequest(requestId: ClusterMemberId): Promise<ClusterView>;
  /**
   * Eject a computer from the cluster.
   *
   * It stays ejected. The record is kept as a tombstone rather than deleted, so
   * a peer that has not yet heard cannot put it back — and a machine that should
   * return has to be admitted again by a human, like any newcomer.
   */
  removeFromCluster(memberId: ClusterMemberId): Promise<ClusterView>;
  /**
   * Pause taking other computers' work, without leaving.
   *
   * This computer stays a member, stays visible, keeps its credentials, and
   * advertises no spare capacity. Its own scanning is unaffected. This is the
   * salesperson on a demo, and re-engaging is one press with nothing to rejoin.
   */
  setClusterParticipating(value: boolean): Promise<ClusterView>;
  /** Leave the cluster. The last member out takes the cluster with them. */
  leaveCluster(): Promise<ClusterView>;
  onClusterChanged(callback: (view: ClusterView) => void): () => void;
};
